import React, { useState, useMemo, useEffect } from "react";
import {
  Brain, Home, Wallet, Factory, Mail, Settings, Boxes,
  TrendingUp, ChevronRight, Power, RotateCw,
  Percent, Activity, Info, Plus, Trash2, Lightbulb,
  X, Sparkles, ArrowUpRight, Zap, ShieldCheck,
  Users, ExternalLink, AlertTriangle, BookOpen, Calculator,
  CheckCircle2, Scale, Gavel, PenLine, Landmark, FileText,
  Clock, MessageSquare, HardDrive, Link2, Receipt, Package, Code2, Eye, XCircle, Lock, Globe, UserCheck, Calendar, Palette, GraduationCap, Circle,
  Film, Atom, Car, Feather, Smile, Trophy, Plane, Wine,
  FileSpreadsheet, Banknote, ClipboardList, PiggyBank, Database, Shirt,
} from "lucide-react";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { api, useAuth } from "../context/AuthContext";
import { PlaybookSection, CureDiary, DoctorCheck } from "../components/VikiDoctor";
import { VikiSuiteSection } from "../components/VikiSuiteHistory";
import { VikiAvatar, VIKI_IMG, VIKI_THEME_IMG, seasonalTheme } from "../components/VikiAvatar";
import { Stethoscope, Phone, FlaskConical, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { AREA_URLS } from "../config/areaUrls";
import { EcosystemDbStatus } from "../components/EcosystemDbStatus";
import { playVikiReply } from "../lib/vikiSounds";

// Token adattati ai CSS custom properties REALI di AREAPAQ (vedi frontend/src/index.css):
// --canvas, --surface, --surface-raised, --surface-border sono già definiti dall'app,
// così VIKI eredita automaticamente il tema chiaro/scuro esistente (.light in index.css)
// senza bisogno di duplicare quella logica qui. cyan/amber/rose/violet restano fissi:
// sono l'identità visiva propria di VIKI, distinta dal resto dell'app (come già scelto
// nel placeholder attuale di VikiPage.js, che usa fuchsia-400 per lo stesso motivo —
// se preferite restare sul fuchsia invece del ciano, è sufficiente cambiare qui i 5 valori).
const C = {
  bg: "var(--canvas)",
  surface: "var(--surface)",
  surfaceAlt: "var(--surface-raised)",
  border: "var(--surface-border)",
  borderSoft: "var(--surface-border)",
  text: "#FFFFFF",
  muted: "rgba(255,255,255,0.88)",
  mutedDim: "rgba(255,255,255,0.68)",
  cyan: "#45E0D0",
  cyanDim: "rgba(69,224,208,0.14)",
  amber: "#F2A93B",
  amberDim: "rgba(242,169,59,0.14)",
  rose: "#E0616F",
  roseDim: "rgba(224,97,111,0.14)",
  violet: "#9C8FF0",
  violetDim: "rgba(156,143,240,0.14)",
};

const mono = { fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace" };

function GlobalStyle() {
  return (
    <style>{`
      @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      .mind-card { transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease; animation: fadeUp 0.5s ease backwards; }
      .mind-card:hover { border-color: #45E0D066 !important; box-shadow: 0 0 0 1px #45E0D022, 0 16px 36px -14px #45E0D044; transform: translateY(-2px); }
      .mind-btn { transition: all 0.2s ease; }
      .mind-btn:hover { filter: brightness(1.15); transform: translateY(-1px); }
      .mind-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
      .mind-scrollbar::-webkit-scrollbar-thumb { background: #22303C; border-radius: 3px; }
      input.mind-input, textarea.mind-input, select.mind-input { background: ${C.surfaceAlt}; border: 1px solid ${C.border}; color: ${C.text}; }
      input.mind-input::placeholder, textarea.mind-input::placeholder { color: ${C.mutedDim}; }
      input.mind-input:focus, textarea.mind-input:focus, select.mind-input:focus { outline: none; border-color: ${C.cyan}88; }
      button:focus-visible, input.mind-input:focus-visible, textarea.mind-input:focus-visible, select.mind-input:focus-visible, a:focus-visible {
        outline: 2px solid ${C.cyan}; outline-offset: 2px; border-radius: 4px;
      }
      @media (prefers-reduced-motion: reduce) {
        .mind-card, .mind-btn { animation: none !important; transition: none !important; }
      }
    `}</style>
  );
}

/* ---------- persistenza reale (storage condiviso dell'artifact) ---------- */
function usePersistedList(key, defaultValue) {
  const [items, setItems] = useState(null);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/viki/data/${key}`);
        if (!cancelled) setItems(data.value ?? defaultValue);
      } catch {
        // 404 = chiave non ancora salvata (prima apertura): parte dal default,
        // senza scrivere nulla finché l'utente non modifica qualcosa davvero.
        if (!cancelled) setItems(defaultValue);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const persist = (updater) => {
    setItems((current) => {
      const next = typeof updater === "function" ? updater(current) : updater;
      api.post(`/viki/data/${key}`, { value: next })
        .then(() => setSaveError(null))
        .catch(() => setSaveError("Salvataggio non riuscito, riprova"));
      return next;
    });
  };

  return { items, persist, loading: items === null, saveError };
}

function LoadingRow() {
  return (
    <div className="flex items-center gap-2 py-8 justify-center" style={{ color: C.mutedDim, fontSize: 12.5 }}>
      <RotateCw size={13} className="animate-spin" /> Caricamento…
    </div>
  );
}

/* ---------- signature element: lo stesso "cervello pensante" di Viki_Schedina ----------
   Stessa silhouette anatomica, stessa rete di sinapsi: è la stessa intelligenza di
   base, applicata qui a un dominio diverso (l'azienda) invece che alle partite. */
function ThinkingBrain({ size = 320, active = true }) {
  const nodes = useMemo(() => {
    const pts = [
      [160, 46], [118, 58], [202, 58], [90, 84], [230, 84],
      [140, 78], [180, 78], [70, 118], [250, 118], [104, 132],
      [216, 132], [160, 108], [128, 158], [192, 158], [160, 182],
      [86, 158], [234, 158], [160, 208], [130, 200], [190, 200],
    ];
    return pts.map(([x, y], i) => ({ x, y, i }));
  }, []);

  const edges = useMemo(() => {
    const e = [];
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const dx = nodes[i].x - nodes[j].x;
        const dy = nodes[i].y - nodes[j].y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < 50) e.push([nodes[i], nodes[j], d, i * 31 + j]);
      }
    }
    return e;
  }, [nodes]);

  const pulseEdges = edges.filter((_, i) => i % 3 === 0);

  return (
    <div style={{ width: size, height: size, position: "relative" }}>
      <style>{`
        @keyframes brainBreathe { 0%,100% { transform: scale(1); filter: drop-shadow(0 0 18px rgba(69,224,208,0.35)); } 50% { transform: scale(1.015); filter: drop-shadow(0 0 32px rgba(69,224,208,0.55)); } }
        @keyframes nodeFire { 0%,100% { opacity: 0.3; r: 2.2; } 50% { opacity: 1; r: 3.8; } }
        @keyframes edgeGlow { 0%,100% { stroke-opacity: 0.08; } 50% { stroke-opacity: 0.4; } }
        @keyframes signalTravel { 0% { stroke-dashoffset: 40; opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { stroke-dashoffset: 0; opacity: 0; } }
        @keyframes sonarRing { 0% { r: 60; opacity: 0.5; } 100% { r: 165; opacity: 0; } }
        .brain-shell { animation: brainBreathe 5s ease-in-out infinite; transform-origin: center; }
        .brain-node { animation: nodeFire 2.8s ease-in-out infinite; }
        .brain-edge { animation: edgeGlow 3.6s ease-in-out infinite; }
        .brain-signal { stroke-dasharray: 10 30; animation: signalTravel 2.4s linear infinite; }
      `}</style>
      <svg viewBox="0 0 320 320" width="100%" height="100%">
        <defs>
          <radialGradient id="coreGlow2" cx="50%" cy="45%" r="55%">
            <stop offset="0%" stopColor={C.cyan} stopOpacity="0.22" />
            <stop offset="60%" stopColor={C.cyan} stopOpacity="0.06" />
            <stop offset="100%" stopColor={C.cyan} stopOpacity="0" />
          </radialGradient>
          <linearGradient id="shellGrad2" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.cyan} stopOpacity="0.95" />
            <stop offset="100%" stopColor={C.cyan} stopOpacity="0.45" />
          </linearGradient>
        </defs>

        <circle cx="160" cy="150" r="60" fill="url(#coreGlow2)" />
        {active && [0, 1.3, 2.6].map((delay) => (
          <circle key={delay} cx="160" cy="150" r="60" fill="none" stroke={C.cyan} strokeWidth="1"
            style={{ animation: `sonarRing 3.9s ease-out infinite`, animationDelay: `${delay}s` }} />
        ))}

        <g className={active ? "brain-shell" : ""}>
          <path
            d="M160 44 C122 40 92 58 82 88 C58 92 44 116 50 142 C40 156 42 180 58 194 C54 214 68 232 90 238 C96 256 116 268 138 264 C148 274 172 274 182 264 C204 268 224 256 230 238 C252 232 266 214 262 194 C278 180 280 156 270 142 C276 116 262 92 238 88 C228 58 198 40 160 44 Z"
            fill="none" stroke="url(#shellGrad2)" strokeWidth="2.5" strokeLinejoin="round"
          />
          <path d="M160 50 C158 90 164 130 160 168 C157 200 163 232 160 258" fill="none" stroke={C.cyan} strokeOpacity="0.5" strokeWidth="1.4" />
          {[
            "M96 82 C104 90 108 100 102 110", "M224 82 C216 90 212 100 218 110",
            "M78 130 C90 134 98 142 92 152", "M242 130 C230 134 222 142 228 152",
            "M100 176 C110 182 114 192 106 200", "M220 176 C210 182 206 192 214 200",
            "M132 232 C138 240 148 244 156 240", "M188 232 C182 240 172 244 164 240",
          ].map((d, i) => (
            <path key={i} d={d} fill="none" stroke={C.cyan} strokeOpacity="0.28" strokeWidth="1.2" />
          ))}
        </g>

        {edges.map(([a, b], idx) => (
          <line key={`e${idx}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={C.cyan} strokeWidth="1"
            className={active ? "brain-edge" : ""} style={{ animationDelay: `${(idx % 7) * 0.3}s`, strokeOpacity: active ? undefined : 0.08 }} />
        ))}
        {active && pulseEdges.map(([a, b], idx) => (
          <line key={`p${idx}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={C.text} strokeWidth="1.6" strokeLinecap="round"
            className="brain-signal" style={{ animationDelay: `${(idx % 5) * 0.5}s` }} />
        ))}
        {nodes.map((n) => (
          <circle key={n.i} cx={n.x} cy={n.y} r="2.8" fill={C.text} className={active ? "brain-node" : ""}
            style={{ animationDelay: `${(n.i % 9) * 0.22}s`, opacity: active ? undefined : 0.4 }} />
        ))}
      </svg>
    </div>
  );
}

/* ---------- indicatore di crescita: stessa logica di Viki_Schedina ---------- */
const GROWTH_METRICS = [
  { key: "sviluppo", label: "Sviluppo", desc: "funzioni, strumenti e integrazioni attive nell'ecosistema", value: 0.96, color: C.amber, radius: 200 },
  { key: "apprendimento", label: "Apprendimento", desc: "domini appresi: ecosistema PAQ, food, amministrazione, contabilità, tesoreria", value: 0.98, color: C.cyan, radius: 178 },
  { key: "ragionamento", label: "Ragionamento", desc: "ragionamento pieno: catene fino a 5 strumenti sui dati reali dei moduli", value: 1.0, color: C.violet, radius: 156 },
  { key: "memoria", label: "Memoria", desc: "memoria personale estesa: date, preferenze e contesti", value: 0.94, color: C.rose, radius: 134 },
];
const GROWTH_DELTA_WEEKLY = 0.05;
const GROWTH_STAGES = [
  { min: 0, label: "Embrionale" }, { min: 0.2, label: "Apprendista" }, { min: 0.4, label: "In sviluppo" },
  { min: 0.6, label: "Competente" }, { min: 0.8, label: "Esperto" },
];
function growthStage(avg) { return [...GROWTH_STAGES].reverse().find((s) => avg >= s.min).label; }

const GROWTH_TIMELINE = [
  { phase: 1, label: "Embrionale", threshold: 0, detail: "Attivazione iniziale di VIKI_AreaPaq: prime Funzioni e Suggerimenti caricati, primo dialogo al login" },
  { phase: 2, label: "Apprendista", threshold: 0.2, detail: "Serve raggiungere il 20% di crescita media: Funzioni e Integrazioni ancora da consolidare" },
  { phase: 3, label: "In sviluppo", threshold: 0.4, detail: "Richiede il 40% di crescita media" },
  { phase: 4, label: "Competente", threshold: 0.6, detail: "Richiede il 60% di crescita media" },
  { phase: 5, label: "Esperto", threshold: 0.8, detail: "Richiede l'80% di crescita media" },
];

function GrowthRing({ metric, index }) {
  const c = 2 * Math.PI * metric.radius;
  const offset = c * (1 - metric.value);
  const animName = `growIn2_${metric.key}`;
  return (
    <>
      <style>{`@keyframes ${animName} { from { stroke-dashoffset: ${c}; } to { stroke-dashoffset: ${offset}; } }`}</style>
      <circle cx="210" cy="210" r={metric.radius} fill="none" stroke={C.borderSoft} strokeWidth="7" />
      <circle cx="210" cy="210" r={metric.radius} fill="none" stroke={metric.color} strokeWidth="7" strokeLinecap="round"
        strokeDasharray={c} transform="rotate(-90 210 210)"
        style={{ filter: `drop-shadow(0 0 5px ${metric.color}99)`, strokeDashoffset: offset,
          animation: `${animName} 1.6s cubic-bezier(0.16,1,0.3,1) ${index * 0.15}s both` }} />
    </>
  );
}

function GrowthIndicator() {
  const avg = GROWTH_METRICS.reduce((s, m) => s + m.value, 0) / GROWTH_METRICS.length;
  const stage = growthStage(avg);
  const stageIdx = GROWTH_STAGES.findIndex((s) => s.label === stage) + 1;
  const brainSize = 200;
  return (
    <div className="flex flex-col items-center gap-4 py-2">
      <div className="flex flex-col items-center" data-testid="viki-identity-title">
        <span style={{ color: C.text, fontSize: 44, fontWeight: 800, letterSpacing: 6, lineHeight: 1.1, ...mono }}>V.I.K.I.</span>
        <span style={{ color: C.cyan, fontSize: 11.5, fontWeight: 600, letterSpacing: 4, textTransform: "uppercase", marginTop: 6, textAlign: "center", ...mono }}>
          Virtual Intelligence for Knowledge &amp; Inference
        </span>
      </div>
      <div className="flex items-center gap-3">
        <span style={{ color: C.text, fontSize: 13, fontWeight: 700, letterSpacing: 0.2, ...mono }}>Fase {stageIdx}/5 · {stage}</span>
        <span style={{ color: C.mutedDim, fontSize: 11, ...mono }}>· crescita {Math.round(avg * 100)}%</span>
        <div className="flex items-center gap-1 px-2.5 py-1 rounded-full" style={{ background: C.cyanDim, border: `1px solid ${C.cyan}44` }}>
          <TrendingUp size={11} style={{ color: C.cyan }} />
          <span style={{ color: C.cyan, fontSize: 11, fontWeight: 700, ...mono }}>+{Math.round(GROWTH_DELTA_WEEKLY * 100)}%</span>
          <span style={{ color: C.mutedDim, fontSize: 10 }}>questa settimana</span>
        </div>
      </div>
      <div style={{ width: 420, height: 420, position: "relative" }}>
        <div style={{
          position: "absolute", inset: -40, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(69,224,208,0.10) 0%, rgba(156,143,240,0.06) 45%, transparent 72%)",
          filter: "blur(4px)", pointerEvents: "none",
        }} />
        <svg viewBox="0 0 420 420" width="100%" height="100%" style={{ position: "absolute", inset: 0 }}>
          {GROWTH_METRICS.map((m, i) => <GrowthRing key={m.key} metric={m} index={i} />)}
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img src="/viki/viki-idle.png" alt="V.I.K.I." draggable={false} className="viki-floaty"
            style={{ width: brainSize * 0.82, height: brainSize * 0.82, objectFit: "contain", filter: "drop-shadow(0 14px 30px rgba(0,0,0,0.5)) drop-shadow(0 0 24px rgba(69,224,208,0.25))" }} />
        </div>
      </div>
      <div className="flex gap-4 mt-1 flex-wrap justify-center" style={{ maxWidth: 560 }}>
        {GROWTH_METRICS.map((m) => (
          <div key={m.key} className="flex flex-col items-center" style={{ maxWidth: 125 }}>
            <div className="flex items-center gap-1.5">
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: m.color, boxShadow: `0 0 6px ${m.color}` }} />
              <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>{m.label}</span>
              <span style={{ color: m.color, fontSize: 12, ...mono }}>{Math.round(m.value * 100)}%</span>
            </div>
            <span style={{ color: C.mutedDim, fontSize: 10.5, textAlign: "center", marginTop: 2 }}>{m.desc}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- cronologia della crescita: racconta le fasi come una storia,
   non solo come un numero percentuale ---------- */
function GrowthTimeline() {
  const avg = GROWTH_METRICS.reduce((s, m) => s + m.value, 0) / GROWTH_METRICS.length;
  return (
    <Card style={{ padding: 20 }}>
      <div className="flex items-center gap-2 mb-4">
        <Clock size={15} style={{ color: C.cyan }} />
        <span style={{ color: C.text, fontSize: 13.5, fontWeight: 600 }}>Cronologia della crescita</span>
      </div>
      <div className="flex flex-col">
        {GROWTH_TIMELINE.map((t, i) => {
          const reached = avg >= t.threshold;
          return (
          <div key={t.phase} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div style={{
                width: 12, height: 12, borderRadius: "50%",
                background: reached ? C.cyan : C.surfaceAlt,
                border: `2px solid ${reached ? C.cyan : C.border}`,
                boxShadow: reached ? `0 0 8px ${C.cyan}88` : "none",
              }} />
              {i < GROWTH_TIMELINE.length - 1 && <div style={{ width: 2, flex: 1, minHeight: 30, background: reached ? C.cyan : C.border, opacity: reached ? 0.4 : 1 }} />}
            </div>
            <div style={{ paddingBottom: 18 }}>
              <div className="flex items-center gap-2 flex-wrap">
                <span style={{ color: reached ? C.text : C.mutedDim, fontSize: 12.5, fontWeight: 700 }}>Fase {t.phase} · {t.label}</span>
                <span style={{
                  color: reached ? C.cyan : C.mutedDim, fontSize: 10, background: reached ? C.cyanDim : C.surfaceAlt,
                  padding: "1px 8px", borderRadius: 20, fontWeight: 600,
                }}>{reached ? "Raggiunta" : "Da raggiungere"}</span>
              </div>
              <div style={{ color: C.mutedDim, fontSize: 11.5, marginTop: 3, lineHeight: 1.5 }}>{t.detail}</div>
            </div>
          </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ---------- modalità radar: colpo d'occhio sullo stato di ogni modulo,
   diverso dalla mappa a sinapsi — qui conta lo stato, non la relazione ---------- */
function MemoryCard() {
  const [facts, setFacts] = useState(null);
  const load = () => api.get("/viki/memory").then(({ data }) => setFacts(data.facts || [])).catch(() => setFacts([]));
  useEffect(() => { load(); }, []);

  const forget = async (id) => {
    try {
      await api.delete(`/viki/memory/${id}`);
      toast.success("Fatto cancellato dalla memoria");
      load();
    } catch {
      toast.error("Operazione non riuscita");
    }
  };

  return (
    <Card style={{ padding: 20 }} data-testid="memory-card">
      <div className="flex items-center gap-2 mb-1">
        <FlaskConical size={15} style={{ color: C.cyan }} />
        <span style={{ color: C.text, fontSize: 13.5, fontWeight: 600 }}>Cosa ricorda VIKI di te</span>
      </div>
      <p style={{ color: C.mutedDim, fontSize: 11.5, marginBottom: 12 }}>
        I fatti che hai confermato in chat: situazioni aperte, contesti ricorrenti e preferenze importanti.
        VIKI li usa per dare continuità alle conversazioni ("l'ultima volta avevi detto che…"). Nulla entra
        qui senza la tua conferma, e puoi cancellare qualsiasi voce quando vuoi.
      </p>
      {facts === null ? (
        <div style={{ color: C.mutedDim, fontSize: 11.5 }}>Carico…</div>
      ) : facts.length === 0 ? (
        <div style={{ color: C.mutedDim, fontSize: 11.5 }} data-testid="memory-empty">
          Ancora nessun fatto confermato: quando in chat emergerà qualcosa che merita di durare, VIKI ti proporrà di salvarlo.
        </div>
      ) : (
        <div className="space-y-2" data-testid="memory-list">
          {facts.map((f) => (
            <div key={f.id} className="flex items-center justify-between gap-3 rounded-md px-3 py-2"
              style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{f.fact}</div>
                <div style={{ color: C.mutedDim, fontSize: 10.5 }}>
                  {f.category || "contesto"}{f.created_at ? ` · salvato il ${new Date(f.created_at).toLocaleDateString("it-IT")}` : ""}
                </div>
              </div>
              <button onClick={() => forget(f.id)} title="Cancella questo fatto"
                data-testid={`memory-forget-${f.id}`}
                className="shrink-0 p-1.5 rounded-md transition-colors hover:bg-white/10">
                <Trash2 size={13} style={{ color: C.rose }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function HabitsCard() {
  const [habits, setHabits] = useState(null);
  const load = () => api.get("/viki/habits").then(({ data }) => setHabits(data.habits || [])).catch(() => setHabits([]));
  useEffect(() => { load(); }, []);

  const forget = async (id) => {
    try {
      await api.delete(`/viki/habits/${id}`);
      toast.success("Abitudine dimenticata");
      load();
    } catch {
      toast.error("Operazione non riuscita");
    }
  };

  const learned = (habits || []).filter((h) => h.learned);
  const observing = (habits || []).filter((h) => !h.learned);
  return (
    <Card style={{ padding: 20 }} data-testid="habits-card">
      <div className="flex items-center gap-2 mb-1">
        <Lightbulb size={15} style={{ color: C.violet }} />
        <span style={{ color: C.text, fontSize: 13.5, fontWeight: 600 }}>Abitudini apprese</span>
      </div>
      <p style={{ color: C.mutedDim, fontSize: 11.5, marginBottom: 12 }}>
        VIKI osserva le richieste che ripeti (consultazioni, indagini, documenti, promemoria): quando una diventa
        ricorrente la impara e la usa per anticiparti, proponendo lei il passo giusto al momento giusto. Qui puoi
        vederle e farle dimenticare.
      </p>
      {habits === null ? (
        <div style={{ color: C.mutedDim, fontSize: 11.5 }}>Carico…</div>
      ) : learned.length === 0 ? (
        <div style={{ color: C.mutedDim, fontSize: 11.5 }} data-testid="habits-empty">
          VIKI sta ancora osservando: nessuna richiesta è ancora diventata un'abitudine (servono almeno 3 ripetizioni).
        </div>
      ) : (
        <div className="space-y-2" data-testid="habits-list">
          {learned.map((h) => (
            <div key={h.id} className="flex items-center justify-between gap-3 rounded-md px-3 py-2"
              style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{h.label}</div>
                <div style={{ color: C.mutedDim, fontSize: 10.5 }}>
                  {h.count} volte{h.typical_hour != null ? ` · di solito verso le ${h.typical_hour}:00` : ""}
                  {h.last_at ? ` · ultima: ${new Date(h.last_at).toLocaleDateString("it-IT")}` : ""}
                </div>
              </div>
              <button onClick={() => forget(h.id)} title="Dimentica questa abitudine"
                data-testid={`habit-forget-${h.id}`}
                className="shrink-0 p-1.5 rounded-md transition-colors hover:bg-white/10">
                <Trash2 size={13} style={{ color: C.rose }} />
              </button>
            </div>
          ))}
        </div>
      )}
      {observing.length > 0 && (
        <div style={{ color: C.mutedDim, fontSize: 10.5, marginTop: 10 }} data-testid="habits-observing">
          In osservazione: {observing.length} richiest{observing.length === 1 ? "a" : "e"} non ancora ricorrent{observing.length === 1 ? "e" : "i"}.
        </div>
      )}
    </Card>
  );
}

function RadarStrip({ items }) {
  const list = (items || PAQ_INTEGRATIONS).filter((it) => it.name !== "WhatsApp Business");
  return (
    <Card style={{ padding: 20 }}>
      <div className="flex items-center gap-2 mb-1">
        <Activity size={15} style={{ color: C.cyan }} />
        <span style={{ color: C.text, fontSize: 13.5, fontWeight: 600 }}>Stato moduli</span>
      </div>
      <p style={{ color: C.mutedDim, fontSize: 11.5, marginBottom: 12 }}>
        I moduli PAQ collegati al cervello di VIKI: verde = VIKI può leggerne i dati e agire, grigio = ancora da collegare.
      </p>
      <style>{`@keyframes radarPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } } .radar-dot-live { animation: radarPulse 1.8s ease-in-out infinite; }`}</style>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
        {list.map((it) => {
          const live = it.status === "Connesso";
          return (
            <div key={it.id} className="flex items-center gap-2 p-2.5 rounded-md" style={{ background: C.surfaceAlt }} title={it.detail}>
              <span className={live ? "radar-dot-live" : ""} style={{ width: 8, height: 8, borderRadius: "50%", background: live ? C.cyan : C.mutedDim, boxShadow: live ? `0 0 6px ${C.cyan}` : "none", flexShrink: 0 }} />
              <it.icon size={13} style={{ color: live ? C.text : C.mutedDim, flexShrink: 0 }} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", color: live ? C.text : C.mutedDim, fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.name}</span>
                <span style={{ display: "block", color: live ? C.cyan : C.mutedDim, fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6 }}>
                  {live ? "Collegato a VIKI" : "Da collegare"}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ================= dati dimostrativi ================= */

const FUNCTION_CATEGORIES = {
  "Contabilità": Receipt, "Comunicazioni": Mail, "Marketing": TrendingUp,
  "Legale": Gavel, "Produzione": Package, "Economia": Landmark, "Informatica": Code2, "Design": Palette, "Generale": Sparkles,
};

const FUNCTIONS = [
  { id: "f1", text: "Se una fattura supera i 30 giorni dalla scadenza, segnala in Tesoreria come \"sollecito da inviare\"", category: "Contabilità", status: "In apprendimento", applied: 3, reason: "Applicata 3 volte, esito confermato manualmente ogni volta: non ancora abbastanza casi per considerarla stabile", source: "3 casi osservati in Tesoreria, incrociati con le scadenze fatture" },
  { id: "f2", text: "Quando arriva una mail da un utente Master, rispondi confermando ricezione entro 2 ore e crea un promemoria di follow-up a 48 ore", category: "Comunicazioni", status: "Appresa", applied: 14, reason: "14 applicazioni senza correzioni da parte tua: comportamento considerato stabile", source: "14 scambi email reali su Posta AI" },
  { id: "f3", text: "Nei report mensili, arrotonda sempre gli importi a due decimali con separatore delle migliaia italiano", category: "Contabilità", status: "Appresa", applied: 22, reason: "Regola formale, verificabile in modo diretto: nessun margine di ambiguità nei 22 report generati", source: "22 report mensili generati, nessuna imprecisione rilevata" },
  { id: "f4", text: "Per le comunicazioni ai clienti usa sempre il tono formale del \"Lei\", mai il \"tu\"", category: "Comunicazioni", status: "Appresa", applied: 31, reason: "Nessuna correzione ricevuta in 31 comunicazioni: il tono è stato sempre confermato adeguato", source: "31 comunicazioni ai clienti, verificate manualmente da te" },
  { id: "f5", text: "Prima di proporre un testo pubblicitario, verifica che rispetti le indicazioni del Codice del Consumo su pubblicità ingannevole", category: "Legale", status: "Da verificare", applied: 0, reason: "Materia legale ad alto rischio: nessuna applicazione autonoma finché non confermi manualmente almeno un caso", source: "Nessun caso reale ancora applicato: solo verifica in Sandbox" },
  { id: "f6", text: "Nei contratti standard, evidenzia sempre in modo distinto le clausole vessatorie ai sensi degli artt. 1341-1342 c.c.", category: "Legale", status: "Da verificare", applied: 0, reason: "Riferimento normativo specifico: in attesa di una tua verifica prima di applicarla ai contratti reali", source: "Nessun caso reale ancora applicato: solo verifica in Sandbox" },
  { id: "f7", text: "Generare newsletter con template accattivanti, adattati allo scopo indicato (promozionale, informativa, fidelizzazione) e al risultato atteso", category: "Marketing", status: "Appresa", applied: 0, reason: "Capacità nativa attivata di default, non richiede apprendimento da funzioni insegnate manualmente" },
  { id: "f8", text: "Leggere gli estratti conto caricati e aggiornare i conti corrispondenti in Contabilità, segnalando dove serve una verifica manuale", category: "Contabilità", status: "Appresa", applied: 0, reason: "Capacità nativa attivata di default; le scritture più incerte restano comunque da confermare a mano" },
  { id: "f9", text: "Se il fatturato risulta sotto l'obiettivo, proporre azioni concrete per recuperare lo scostamento (prezzi, clienti inattivi, campagne da riattivare)", category: "Economia", status: "Appresa", applied: 0, reason: "Capacità nativa attivata di default, collegata alla Conoscenza di Economia e ai dati di Performance" },
  { id: "f10", text: "Quando viene condiviso codice dei moduli PAQ, segnalare bug, rischi di sicurezza e miglioramenti, proponendo una correzione pronta da confrontare riga per riga", category: "Informatica", status: "Appresa", applied: 0, reason: "Capacità nativa attivata di default, collegata alla Conoscenza di Informatica & programmazione" },
  { id: "f11", text: "Generare il bilancio provvisorio periodico (mensile o trimestrale), con stato patrimoniale e conto economico aggiornati, per il controllo interno", category: "Contabilità", status: "Appresa", applied: 0, reason: "Capacità nativa attivata di default; resta un documento di controllo interno, non da inviare a terzi" },
  { id: "f12", text: "Generare il bilancio finale in formato professionale, secondo lo schema civilistico, pronto per l'invio a banche e istituti di credito", category: "Contabilità", status: "Appresa", applied: 0, reason: "Capacità nativa attivata di default; l'invio effettivo alla banca resta sempre una tua conferma esplicita" },
  { id: "f13", text: "Quando si progetta o modifica l'interfaccia di un modulo PAQ, verificare la coerenza con palette, tipografia e componenti già in uso nell'ecosistema, segnalando eventuali disallineamenti", category: "Design", status: "Appresa", applied: 0, reason: "Capacità nativa attivata di default, per mantenere un'identità visiva unica su tutti i moduli PAQ" },
  { id: "f15", text: "Quando viene condiviso il verbale o la registrazione di una riunione, estrarre decisioni prese e prossimi passi, smistandoli alle Funzioni o a Governance", category: "Generale", status: "Appresa", applied: 0, reason: "Capacità nativa attivata di default; le decisioni estratte restano comunque da confermare nelle sezioni di destinazione" },
  { id: "f16", text: "Confermare in autonomia la ricezione dei pagamenti tracciati quando l'importo coincide esattamente con la fattura in attesa", category: "Contabilità", status: "In apprendimento", applied: 10, reason: "10 applicazioni consecutive senza correzioni: ha raggiunto la soglia minima per proporre l'avanzamento a \"Appresa\"", source: "10 pagamenti tracciati confrontati con gli estratti conto caricati" },
  { id: "f17", text: "Tradurre testi e comunicazioni tra italiano e le lingue conosciute (inglese, francese, spagnolo, cinese), mantenendo tono e stile richiesti", category: "Comunicazioni", status: "Appresa", applied: 0, reason: "Capacità nativa attivata di default, collegata alla Conoscenza di Lingue" },
  { id: "f18", text: "Quando si pubblica un nuovo contenuto per un canale PAQ, suggerire parole chiave SEO e verificare titolo, meta-descrizione e struttura per la visibilità organica", category: "Marketing", status: "Appresa", applied: 0, reason: "Capacità nativa attivata di default, collegata al dettaglio SEO in Conoscenza → Marketing" },
  { id: "f19", text: "Proporre bozze di annunci a pagamento (SEM e social ads), coerenti con budget e obiettivo indicati, da confermare prima della pubblicazione", category: "Marketing", status: "Appresa", applied: 0, reason: "Capacità nativa attivata di default; la spesa pubblicitaria resta sempre soggetta a conferma in Governance" },
  { id: "f20", text: "Scrivere e rispondere alle email in italiano o nella lingua del destinatario, con tono pacato e professionale, senza toni aggressivi anche in situazioni tese (reclami, solleciti, ritardi)", category: "Comunicazioni", status: "Appresa", applied: 0, reason: "Capacità nativa attivata di default, collegata a Lingua italiana, Lingue straniere e Scrittura" },
];

const FUNCTION_STATUS_STYLE = {
  "Appresa": { color: C.cyan, bg: C.cyanDim },
  "In apprendimento": { color: C.amber, bg: C.amberDim },
  "Da verificare": { color: C.mutedDim, bg: C.surfaceAlt },
};

const SANDBOX_CASES = {
  f1: {
    caseText: "Fattura n. 2026-0091 scaduta da 34 giorni, ma il cliente ha uno storico di pagamenti sempre puntuali",
    result: "VIKI segnalerebbe comunque \"sollecito da inviare\", senza considerare lo storico di puntualità del cliente",
    verdict: "Da correggere",
  },
  f5: {
    caseText: "Bozza campagna \"Sconto WOW: risparmi fino al 70%*\" senza indicare da nessuna parte le condizioni dell'asterisco",
    result: "VIKI segnalerebbe il testo come a rischio pubblicità ingannevole (art. 22 Codice del Consumo) e chiederebbe di rendere visibili le condizioni prima della pubblicazione",
    verdict: "Corretto",
  },
  f6: {
    caseText: "Contratto standard di fornitura con clausola di foro esclusivo a favore del solo fornitore",
    result: "VIKI evidenzierebbe la clausola come potenzialmente vessatoria (art. 1341 c.c.) e richiederebbe la doppia sottoscrizione specifica",
    verdict: "Corretto",
  },
  f7: {
    caseText: "Scopo indicato: newsletter di fidelizzazione per i clienti Master dell'ecosistema PAQ",
    result: "VIKI genererebbe un template pronto, con la stessa identità visiva della Guida di stile PAQ",
    verdict: "Anteprima",
    visual: "newsletter",
  },
  f11: {
    caseText: "Chiusura contabile di agosto 2026, richiesta per il controllo interno mensile",
    result: "VIKI genererebbe il bilancio provvisorio con stato patrimoniale e conto economico aggiornati, pronto per la tua revisione interna",
    verdict: "Anteprima",
  },
  f12: {
    caseText: "La banca chiede il bilancio in vista del rinnovo dell'affidamento",
    result: "VIKI genererebbe il bilancio finale in formato professionale, secondo lo schema civilistico, pronto per l'invio dopo la tua conferma",
    verdict: "Anteprima",
  },
};
const SANDBOX_VERDICT_STYLE = {
  "Corretto": { color: C.cyan, bg: C.cyanDim },
  "Da correggere": { color: C.rose, bg: C.roseDim },
  "Anteprima": { color: C.violet, bg: C.violetDim },
};

const SUGGESTIONS = [
  { id: "s1", text: "Prima di generare un preventivo, controlla sempre lo storico prezzi applicati allo stesso cliente", priority: "Alta", active: true, reason: "Un prezzo incoerente con lo storico rischia di compromettere la fiducia del cliente: priorità alta perché l'errore è visibile subito" },
  { id: "s2", text: "Nelle risposte email, evita testi troppo lunghi: massimo 5 righe nella prima interazione", priority: "Media", active: true, reason: "Migliora la chiarezza ma non è un rischio operativo: priorità media" },
  { id: "s3", text: "Quando il cliente scrive in inglese, rispondi in inglese, senza tradurre automaticamente in italiano", priority: "Media", active: true, reason: "Errore ricorrente osservato più volte, ma correggibile facilmente: priorità media" },
  { id: "s4", text: "Nei promemoria di scadenza, indica sempre il numero di giorni residui, non solo la data", priority: "Bassa", active: false, reason: "Miglioria di leggibilità, nessun impatto sulla correttezza: priorità bassa" },
  { id: "s5", text: "Prima di rispondere a mail urgenti da clienti Master, verifica sempre con il collega di reparto competente", priority: "Alta", active: true, reason: "Un errore su una mail urgente da un cliente importante può costare la relazione: priorità alta" },
];
const PRIORITY_STYLE = {
  Alta: { color: C.rose, bg: C.roseDim }, Media: { color: C.amber, bg: C.amberDim }, Bassa: { color: C.mutedDim, bg: C.surfaceAlt },
};

const KNOWLEDGE_DOMAINS = [
  { key: "calcolo", label: "Calcolo & contabilità", icon: Calculator, value: 0.97, desc: "Aritmetica, IVA, riconciliazioni, arrotondamenti" },
  { key: "lingua_italiana", label: "Lingua italiana", icon: BookOpen, value: 0.98, desc: "Vocabolario, grammatica e ortografia: la base di ogni comunicazione di VIKI" },
  { key: "scrittura", label: "Scrittura", icon: PenLine, value: 0.97, desc: "Email, comunicazioni, report, tono adeguato al contesto" },
  { key: "diritto_privato", label: "Diritto privato", icon: Scale, value: 0.88, desc: "Codice Civile: contratti, clausole, obbligazioni, tutela del consumatore" },
  { key: "diritto_penale", label: "Diritto penale", icon: Gavel, value: 0.82, desc: "Codice Penale: riconoscimento di ambiti a rischio, nessuna consulenza legale sostitutiva" },
  { key: "marketing", label: "Marketing", icon: TrendingUp, value: 0.95, desc: "Campagne, posizionamento, coerenza del messaggio col brand PAQ" },
  { key: "economia", label: "Economia", icon: Landmark, value: 0.96, desc: "Flussi di cassa, margini, indicatori di bilancio" },
  { key: "ecosistema_paq", label: "Ecosistema PAQ", icon: Boxes, value: 0.72, desc: "Funzionalità dei moduli PAQ e dei loro aggiornamenti nel tempo" },
  { key: "informatica", label: "Informatica & programmazione", icon: Code2, value: 0.97, desc: "Linguaggi comuni (JavaScript, Python, SQL, PHP) e logica di sviluppo" },
  { key: "lingue", label: "Lingue straniere", icon: Globe, value: 0.96, desc: "Inglese, francese, spagnolo, cinese — oltre all'italiano, già padroneggiato" },
  { key: "sicurezza", label: "Sicurezza & manutenzione", icon: ShieldCheck, value: 0.60, desc: "Monitoraggio continuo dello stato di salute e sicurezza dei moduli PAQ" },
  { key: "newsletter_design", label: "Design & newsletter", icon: FileText, value: 0.91, desc: "Template accattivanti, adattati allo scopo e al risultato voluto" },
  { key: "web_design", label: "Web design & coerenza visiva", icon: Palette, value: 0.78, desc: "Palette, tipografia e componenti condivisi per uniformare stile e armonia di tutto l'ecosistema PAQ" },
];

/* ---------- Aree di competenza: tassonomia accademica gerarchica,
   più formale e completa dei domini operativi qui sopra. Le due viste
   convivono: quella sopra è "cosa VIKI usa nel lavoro quotidiano",
   questa è "quanto è strutturata la sua preparazione di base". ---------- */
const KNOWLEDGE_AREAS = [
  {
    key: "giuridica", label: "Area Giuridica", icon: Scale,
    groups: [
      { key: "pubblico", label: "Diritto Pubblico", items: [
        { label: "Diritto Costituzionale", desc: "Le regole fondamentali dello Stato e i diritti dei cittadini", value: 0.82 },
        { label: "Diritto Amministrativo", desc: "Il funzionamento della pubblica amministrazione", value: 0.78 },
        { label: "Diritto Penale", desc: "I reati e le relative pene (Codice Penale)", value: 0.74 },
        { label: "Diritto Processuale", desc: "Lo svolgimento dei processi civili, penali e amministrativi", value: 0.72 },
        { label: "Diritto Ecclesiastico", desc: "I rapporti tra Stato e confessioni religiose", value: 0.68 },
      ]},
      { key: "privato", label: "Diritto Privato", items: [
        { label: "Diritto Civile", desc: "Contratti, proprietà, famiglia e successioni (Codice Civile)", value: 0.85 },
        { label: "Diritto Commerciale", desc: "Aziende, società e contratti d'impresa", value: 0.82 },
        { label: "Diritto del Lavoro", desc: "Il rapporto tra datori di lavoro e dipendenti", value: 0.80 },
        { label: "Diritto Industriale", desc: "Proprietà intellettuale, marchi e brevetti", value: 0.76 },
      ]},
      { key: "internazionale", label: "Diritto Internazionale ed Europeo", items: [
        { label: "Diritto Internazionale", desc: "I rapporti tra gli Stati sovrani", value: 0.78 },
        { label: "Diritto dell'Unione Europea", desc: "Le norme comunitarie che vincolano i paesi membri", value: 0.80 },
      ]},
    ],
  },
  {
    key: "economiche", label: "Area Discipline Economiche", icon: Landmark,
    items: [
      { label: "Microeconomia", value: 0.88 },
      { label: "Macroeconomia", value: 0.88 },
      { label: "Politica economica", value: 0.82 },
      { label: "Economia internazionale", value: 0.84 },
      { label: "Econometria", value: 0.78 },
    ],
  },
  {
    key: "aziendali", label: "Area Discipline Aziendali e Contabili", icon: Receipt,
    groups: [
      { key: "bilancio", label: "Bilancio & Rendicontazione", icon: FileSpreadsheet, items: [
        { label: "Stato patrimoniale", desc: "Attivo, passivo e patrimonio netto: la fotografia dell'azienda a una data", value: 0.87 },
        { label: "Conto economico", desc: "Ricavi, costi e risultato d'esercizio del periodo", value: 0.87 },
        { label: "Bilancio provvisorio", desc: "Collegato alla Funzione che lo genera per il controllo interno mensile o trimestrale", value: 0.86 },
        { label: "Bilancio finale per banche", desc: "Collegato alla Funzione che lo prepara in formato professionale per istituti di credito", value: 0.84 },
        { label: "Nota integrativa", desc: "Le informazioni a corredo del bilancio, richieste per legge nelle società di capitali", value: 0.78 },
        { label: "Indici di bilancio", desc: "Liquidità, solidità e redditività: come leggere la salute di un'azienda dai numeri", value: 0.83 },
      ]},
      { key: "conti_correnti", label: "Conti Correnti & Movimenti", icon: Banknote, items: [
        { label: "Lettura estratti conto", desc: "Collegata alla Funzione che legge gli estratti caricati e aggiorna i conti corrispondenti", value: 0.85 },
        { label: "Riconciliazione bancaria", desc: "Far coincidere i movimenti in contabilità con quelli reali del conto corrente", value: 0.84 },
        { label: "Flussi di cassa", desc: "Entrate e uscite reali di liquidità, distinte dal solo risultato contabile", value: 0.85 },
        { label: "Solleciti e scadenze pagamenti", desc: "Collegata alla Funzione che segnala le fatture scadute da sollecitare", value: 0.83 },
      ]},
      { key: "contabilita_operativa", label: "Contabilità Operativa", icon: ClipboardList, items: [
        { label: "Partita doppia", desc: "Il principio base della registrazione contabile: dare e avere", value: 0.86 },
        { label: "Fatturazione attiva e passiva", desc: "Emissione e ricezione di fatture, anche elettroniche", value: 0.85 },
        { label: "IVA e adempimenti fiscali ricorrenti", desc: "Liquidazioni periodiche e scadenze collegate allo Scadenzario", value: 0.82 },
        { label: "Ragioneria generale", desc: "I principi contabili di base applicati alla tenuta dei registri", value: 0.87 },
      ]},
      { key: "finanza_org", label: "Finanza & Organizzazione", icon: PiggyBank, items: [
        { label: "Finanza aziendale", desc: "Investimenti, finanziamenti e struttura del capitale", value: 0.83 },
        { label: "Budget e controllo di gestione", desc: "Pianificare i costi attesi e confrontarli con quelli reali", value: 0.81 },
        { label: "Economia aziendale", desc: "I principi generali su come un'azienda crea e distribuisce valore", value: 0.85 },
        { label: "Organizzazione aziendale", desc: "Struttura, ruoli e processi interni di un'azienda", value: 0.80 },
      ]},
    ],
  },
  {
    key: "quantitativa", label: "Area Quantitativa", icon: Calculator,
    items: [
      { label: "Matematica generale", value: 0.92 },
      { label: "Matematica finanziaria", value: 0.88 },
      { label: "Statistica", value: 0.87 },
      { label: "Analisi dei dati", value: 0.89 },
    ],
  },
  {
    key: "informatica_acc", label: "Area Informatica", icon: Code2,
    items: [
      { label: "Sviluppo Software", desc: "Scrittura di codice, applicazioni, siti web e software gestionali", value: 0.88 },
      { label: "Basi di Dati (Database)", desc: "Organizzazione, archiviazione e gestione sicura di grandi moli di dati", value: 0.86 },
      { label: "Sicurezza Informatica (Cybersecurity)", desc: "Conoscenza solida delle minacce e delle difese; l'esecuzione pratica di un attacco o di una difesa reale resta un ambito dove i modelli attuali sono ancora deboli", value: 0.70 },
      { label: "Reti e Sistemi", desc: "Conoscenza teorica a livello di esperto umano; manca ancora l'accesso diretto a server e reti reali per intervenire davvero", value: 0.65 },
      { label: "Assistenza e Hardware", desc: "Supporto e diagnosi possibili solo a parole: la riparazione fisica di un componente resta un limite strutturale, non di conoscenza", value: 0.28 },
    ],
  },
  {
    key: "marketing_acc", label: "Area Marketing", icon: TrendingUp,
    items: [
      { label: "Analisi", value: 0.84 },
      { label: "Strategia", value: 0.82 },
      { label: "Comunicazione", value: 0.90 },
    ],
  },
  {
    key: "umanistiche", label: "Area Discipline Umanistiche", icon: BookOpen,
    single: { desc: "Raggruppa gli studi classici, la filologia e la storia della lingua.", value: 0.90 },
  },
  {
    key: "linguistiche", label: "Area Scienze Linguistiche", icon: Globe,
    single: { desc: "Studiano la struttura e il funzionamento delle lingue umane.", value: 0.93 },
  },
  {
    key: "mediazione", label: "Area Mediazione Linguistica e Traduttiva", icon: MessageSquare,
    single: { desc: "Tecniche di scrittura avanzata, interpretariato e scienza della traduzione.", value: 0.90 },
  },
  {
    key: "cultura_generale", label: "Area Cultura Generale", icon: BookOpen,
    groups: [
      { key: "storia_geo", label: "Storia & Geografia", icon: Globe, items: [
        { label: "Storia mondiale", desc: "Grandi civiltà, eventi ed epoche, dall'antichità al Novecento", value: 0.95 },
        { label: "Storia dell'arte", desc: "Correnti, autori e opere principali, dal Rinascimento al contemporaneo", value: 0.93 },
        { label: "Geografia fisica e politica", desc: "Territori, confini, capitali, morfologia del pianeta", value: 0.96 },
        { label: "Mitologia", desc: "Miti greci, romani, norreni e di altre culture antiche", value: 0.92 },
        { label: "Storia locale italiana", desc: "Regioni, città, tradizioni e vicende dei territori italiani", value: 0.91 },
        { label: "Grandi esplorazioni", desc: "Viaggi, scoperte geografiche ed esploratori nella storia", value: 0.91 },
      ]},
      { key: "lettere_arti", label: "Lettere & Arti", icon: Feather, items: [
        { label: "Letteratura italiana", desc: "Dai classici al Novecento, autori e opere principali", value: 0.95 },
        { label: "Letteratura straniera", desc: "I grandi nomi della narrativa mondiale, in traduzione", value: 0.93 },
        { label: "Poesia", desc: "Forme, autori e componimenti, italiani e internazionali", value: 0.92 },
        { label: "Pittura e scultura", desc: "Tecniche, autori e movimenti artistici principali", value: 0.93 },
        { label: "Filosofia", desc: "Correnti di pensiero e autori, dall'antichità a oggi", value: 0.94 },
        { label: "Architettura", desc: "Stili, opere ed edifici storici e contemporanei", value: 0.92 },
        { label: "Fotografia", desc: "Storia, tecniche e grandi autori della fotografia", value: 0.88 },
      ]},
      { key: "spettacolo", label: "Spettacolo & Intrattenimento", icon: Film, items: [
        { label: "Cinema", desc: "Registi, film e generi, dal muto al contemporaneo", value: 0.93 },
        { label: "Musica", desc: "Generi, artisti e opere, classica e moderna", value: 0.92 },
        { label: "Teatro", desc: "Drammaturgia, autori e opere principali", value: 0.90 },
        { label: "Serie TV", desc: "Produzioni, generi e titoli più noti, italiani e internazionali", value: 0.89 },
        { label: "Videogiochi", desc: "Generi, titoli storici e attuali, cultura videoludica", value: 0.86 },
      ]},
      { key: "scienze_natura", label: "Scienze & Natura", icon: Atom, items: [
        { label: "Astronomia", desc: "Sistema solare, stelle, galassie e osservazione del cielo", value: 0.94 },
        { label: "Chimica", desc: "Elementi, reazioni e principi di base", value: 0.90 },
        { label: "Biologia", desc: "Corpo umano, natura, animali e piante a livello divulgativo", value: 0.92 },
        { label: "Fisica", desc: "Principi e fenomeni di base, a livello divulgativo", value: 0.91 },
        { label: "Ecologia e ambiente", desc: "Ecosistemi, sostenibilità e cambiamento climatico, a livello divulgativo", value: 0.89 },
      ]},
      { key: "vita_societa", label: "Vita quotidiana & Società", icon: Smile, items: [
        { label: "Gastronomia", desc: "Cucine del mondo, ingredienti, tecniche e abbinamenti", value: 0.91 },
        { label: "Buone maniere", desc: "Galateo e comportamento adeguato al contesto", value: 0.94 },
        { label: "Comportamento umano", desc: "Dinamiche sociali e relazionali di uso comune", value: 0.88 },
        { label: "Medicina generale", desc: "Informazioni divulgative, mai diagnosi: per situazioni reali serve sempre un medico", value: 0.78 },
        { label: "Politica", desc: "Posizioni presentate in modo equilibrato, mai un'opinione personale su temi contestati", value: 0.84 },
        { label: "Psicologia divulgativa", desc: "Concetti generali di uso comune, mai un parere clinico personale", value: 0.82 },
      ]},
      { key: "motori", label: "Motori", icon: Car, items: [
        { label: "Automobili", desc: "Marchi, modelli e caratteristiche tecniche di base", value: 0.90 },
        { label: "Motociclette", desc: "Marchi, modelli e caratteristiche tecniche di base", value: 0.89 },
      ]},
      { key: "sport_gioco", label: "Sport & Giochi", icon: Trophy, items: [
        { label: "Sport principali", desc: "Regole, discipline e grandi eventi sportivi internazionali", value: 0.92 },
        { label: "Enigmistica e giochi da tavolo", desc: "Cruciverba, scacchi, carte e giochi di logica classici", value: 0.91 },
      ]},
      { key: "viaggi_mondo", label: "Viaggi & Culture del mondo", icon: Plane, items: [
        { label: "Turismo e luoghi d'interesse", desc: "Mete, monumenti e itinerari noti nel mondo", value: 0.92 },
        { label: "Religioni e tradizioni", desc: "Principali religioni e usanze culturali nel mondo, in modo neutrale", value: 0.87 },
      ]},
      { key: "gusto_stile", label: "Gusto & Stile", icon: Wine, items: [
        { label: "Vini e bevande", desc: "Vitigni, abbinamenti e cultura enogastronomica di base", value: 0.89 },
        { label: "Moda e stile", desc: "Tendenze, storia della moda e grandi maison, a livello generale", value: 0.85 },
      ]},
    ],
  },
];

function areaAverage(area) {
  if (area.single) return area.single.value;
  if (area.items) return area.items.reduce((s, i) => s + i.value, 0) / area.items.length;
  if (area.groups) {
    const all = area.groups.flatMap((g) => g.items);
    return all.reduce((s, i) => s + i.value, 0) / all.length;
  }
  return 0;
}


const EXECUTION_DATA = [
  { category: "Contabilità", success: 0.91, samples: 22 },
  { category: "Comunicazioni", success: 0.87, samples: 31 },
  { category: "Legale", success: null, samples: 0 },
  { category: "Marketing", success: null, samples: 0 },
];

const AUTONOMY_LEVELS = [
  { area: "Comunicazioni", icon: Mail, level: "Autonoma", desc: "Risponde e conferma ricezione senza attendere approvazione" },
  { area: "Contabilità", icon: Receipt, level: "Richiede conferma", desc: "Prepara solleciti e report, ma serve un tuo via libera prima dell'invio" },
  { area: "Marketing", icon: TrendingUp, level: "Richiede conferma", desc: "Propone testi e campagne, mai pubblicati senza approvazione" },
  { area: "Legale", icon: Gavel, level: "Solo lettura", desc: "Segnala rischi e organizza informazioni, non decide né agisce" },
  { area: "Produzione", icon: Package, level: "Solo lettura", desc: "Ancora nessun collegamento operativo attivo" },
];
const AUTONOMY_STYLE = {
  "Autonoma": { color: C.cyan, bg: C.cyanDim, icon: CheckCircle2 },
  "Richiede conferma": { color: C.amber, bg: C.amberDim, icon: AlertTriangle },
  "Solo lettura": { color: C.mutedDim, bg: C.surfaceAlt, icon: Eye },
};

const DECISION_LOG = [
  { id: "d1", area: "Comunicazioni", action: "Confermata ricezione mail e creato promemoria follow-up per il cliente Rossi Srl", time: "oggi · 09:14", status: "Eseguita", reason: "Comunicazioni è area Autonoma, e la Funzione applicata ha già 14 esiti confermati" },
  { id: "d2", area: "Contabilità", action: "Preparato sollecito per fattura scaduta n. 2026-0184, in attesa di conferma per l'invio", time: "oggi · 08:47", status: "In attesa", reason: "Contabilità richiede conferma: preparato ma non inviato in autonomia" },
  { id: "d3", area: "Comunicazioni", action: "Risposta inviata in inglese a richiesta di un cliente estero", time: "ieri · 17:32", status: "Eseguita", reason: "Applica il Suggerimento attivo su lingua del cliente, priorità Media" },
  { id: "d4", area: "Marketing", action: "Proposta bozza campagna social per il nuovo modulo Risorice", time: "ieri · 15:05", status: "In attesa", reason: "Marketing richiede conferma: solo bozza proposta, nessuna pubblicazione autonoma" },
];
const DECISION_STATUS_STYLE = {
  "Eseguita": { color: C.cyan, bg: C.cyanDim, icon: CheckCircle2 },
  "In attesa": { color: C.amber, bg: C.amberDim, icon: Clock },
  "Annullata": { color: C.rose, bg: C.roseDim, icon: XCircle },
};

const RBAC_PERMISSIONS = [
  { area: "Contabilità, Comunicazioni, Marketing", allowed: true },
  { area: "Governance e log delle decisioni", allowed: true },
  { area: "Buste paga dei dipendenti", allowed: false },
  { area: "Dati bancari personali dei soci", allowed: false },
];

const SECURITY_CHECKS = [
  { id: "sec1", module: "Portale AREAPAQ", check: "Certificato SSL", status: "OK", lastCheck: "oggi · 06:00" },
  { id: "sec2", module: "Posta AI", check: "Backup casella condivisa", status: "OK", lastCheck: "oggi · 04:30" },
  { id: "sec3", module: "Risorice", check: "Versione app rispetto agli store", status: "Da verificare", lastCheck: "3 giorni fa" },
];
const SECURITY_STATUS_STYLE = {
  "OK": { color: C.cyan, bg: C.cyanDim, icon: CheckCircle2 },
  "Da verificare": { color: C.amber, bg: C.amberDim, icon: AlertTriangle },
};

const VIRTUAL_STAFF = [
  { id: "st1", role: "Responsabile Marketing", icon: TrendingUp, focus: "Newsletter, campagne social, coerenza del messaggio col brand PAQ", status: "Attivo", linkedTo: "Conoscenza → Marketing · Funzioni → Newsletter" },
  { id: "st2", role: "Responsabile Commerciale", icon: Users, focus: "Preventivi, storico prezzi, relazione con i clienti Master", status: "Attivo", linkedTo: "Suggerimenti → storico prezzi cliente" },
  { id: "st3", role: "Responsabile di Produzione", icon: Factory, focus: "Coordinamento con il modulo Produzione, tempistiche e scorte", status: "In attesa di collegamento", linkedTo: "Integrazioni PAQ → Produzione" },
  { id: "st4", role: "Responsabile Amministrativo", icon: Receipt, focus: "Contabilità, estratti conto, solleciti, scostamento fatturato", status: "Attivo", linkedTo: "Funzioni → Contabilità, Economia" },
];
const STAFF_STATUS_STYLE = {
  "Attivo": { color: C.cyan, bg: C.cyanDim }, "In attesa di collegamento": { color: C.mutedDim, bg: C.surfaceAlt },
};

const CALL_LOG = [
  { id: "ch1", caller: "Cliente Rossi Srl", time: "ieri · 11:20", outcome: "Fissato appuntamento per il 28/08 alle 15:00", duration: "2 min 10 sec" },
  { id: "ch2", caller: "Numero sconosciuto", time: "ieri · 09:05", outcome: "Richiesta info generali, indirizzata a Posta AI", duration: "1 min 40 sec" },
];

const MEETINGS = [
  {
    id: "mt1", title: "Riunione commerciale settimanale", date: "20 ago 2026",
    decisions: ["Approvato il nuovo listino prezzi per i clienti Master", "Rimandata la campagna autunnale di due settimane"],
    nextSteps: ["Aggiornare i Suggerimenti con il nuovo listino", "Verificare disponibilità grafica per la campagna"],
    routedTo: ["Suggerimenti", "Governance"],
  },
  {
    id: "mt2", title: "Allineamento amministrazione", date: "14 ago 2026",
    decisions: ["Confermata la soglia di 500€ per l'invio automatico dei solleciti"],
    nextSteps: ["Nessuna azione ulteriore: soglia già attiva nelle Funzioni"],
    routedTo: ["Governance"],
  },
];

const CRM_CLIENTS = [
  { id: "cr1", name: "Rossi Srl", status: "Attivo", lastContact: "oggi", nextAction: "Follow-up sull'appuntamento fissato per il 28/08" },
  { id: "cr2", name: "Bianchi & Co", status: "In trattativa", lastContact: "3 giorni fa", nextAction: "Inviare il preventivo aggiornato con il nuovo listino" },
  { id: "cr3", name: "Verdi Costruzioni", status: "Da ricontattare", lastContact: "18 giorni fa", nextAction: "Nessun contatto recente: verificare se il progetto è ancora attivo" },
  { id: "cr4", name: "Studio Neri", status: "Attivo", lastContact: "ieri", nextAction: "Confermare ricezione fattura n. 2026-0184" },
];
const CRM_STATUS_STYLE = {
  "Attivo": { color: C.cyan, bg: C.cyanDim }, "In trattativa": { color: C.amber, bg: C.amberDim }, "Da ricontattare": { color: C.rose, bg: C.roseDim },
};

const SIMULATION_SCENARIOS = [
  {
    id: "sim1", question: "Cosa succede se perdo il cliente più grande?",
    impact: "Stima: -18% sul fatturato mensile, concentrato soprattutto in Contabilità e Produzione",
    recommendation: "Diversificare il portafoglio clienti prima che accada; la Funzione su scostamento fatturato (f9) può già proporre azioni concrete se la soglia viene superata",
  },
  {
    id: "sim2", question: "Cosa succede se rallento le risposte email di 24 ore?",
    impact: "Stima: aumento del rischio di reclami nelle Comunicazioni, area oggi Autonoma in Governance",
    recommendation: "Valutare se spostare temporaneamente Comunicazioni da Autonoma a Richiede conferma, per tenere il controllo più stretto",
  },
  {
    id: "sim3", question: "Cosa succede se attivo tutte le Funzioni ancora 'Da verificare'?",
    impact: "2 Funzioni in area Legale verrebbero applicate senza conferma umana su materia ad alto rischio",
    recommendation: "Meglio verificarle una alla volta con la Sandbox prima di attivarle tutte insieme",
  },
];


const ONBOARDING_STEPS = [
  { id: "ob1", title: "Accesso al Portale AREAPAQ", desc: "Attivazione dell'utenza e primo accesso, con riconoscimento automatico via SSO su tutti i moduli collegati", done: true },
  { id: "ob2", title: "Regole di tono nelle comunicazioni", desc: "Con i clienti si usa sempre il \"Lei\"; VIKI lo ricorda automaticamente in Posta AI, ma è bene saperlo da subito", done: true },
  { id: "ob3", title: "Dove trovare Funzioni e Suggerimenti", desc: "È lì che si insegna a VIKI come lavora il reparto: vale la pena dare un'occhiata prima di scrivere le prime email", done: false },
  { id: "ob4", title: "Limiti di autonomia in Governance", desc: "Quali aree VIKI gestisce da sola e quali richiedono conferma, per capire dove serve intervenire", done: false },
  { id: "ob5", title: "Scadenzario attivo", desc: "Le scadenze fiscali e normative in corso, per non partire scoperti sulle prossime settimane", done: false },
];

const DEADLINE_CATEGORIES = {
  "Fiscale": Receipt, "Normativo": Scale, "Sicurezza": Lock, "Amministrativo": FileText,
};
const DEADLINES = [
  { id: "dl1", item: "Liquidazione IVA trimestrale", category: "Fiscale", dueDate: "16 set 2026", daysLeft: 24, urgency: "Urgente" },
  { id: "dl2", item: "Versamento F24 ritenute", category: "Fiscale", dueDate: "16 set 2026", daysLeft: 24, urgency: "Urgente" },
  { id: "dl3", item: "Rinnovo consenso privacy clienti (GDPR)", category: "Normativo", dueDate: "30 set 2026", daysLeft: 38, urgency: "In arrivo" },
  { id: "dl4", item: "Rinnovo certificato SSL portale AREAPAQ", category: "Sicurezza", dueDate: "15 ott 2026", daysLeft: 53, urgency: "Programmata" },
  { id: "dl5", item: "Scadenza contratto fornitore hosting", category: "Amministrativo", dueDate: "1 nov 2026", daysLeft: 70, urgency: "Programmata" },
];
const DEADLINE_URGENCY_STYLE = {
  "Urgente": { color: C.rose, bg: C.roseDim }, "In arrivo": { color: C.amber, bg: C.amberDim }, "Programmata": { color: C.mutedDim, bg: C.surfaceAlt },
};

const FINANCIAL_ANOMALIES = [
  { id: "an1", description: "Due pagamenti identici da 1.240€ allo stesso fornitore nello stesso giorno", area: "Contabilità", severity: "Alta" },
  { id: "an2", description: "Fattura emessa a un cliente con importo fuori norma rispetto al suo storico (+340%)", area: "Contabilità", severity: "Media" },
  { id: "an3", description: "Movimento contabile registrato alle 03:14, fuori dal normale orario operativo", area: "Contabilità", severity: "Media" },
];
const ANOMALY_SEVERITY_STYLE = {
  "Alta": { color: C.rose, bg: C.roseDim }, "Media": { color: C.amber, bg: C.amberDim }, "Bassa": { color: C.mutedDim, bg: C.surfaceAlt },
};

const MONITORED_ACTIVITY = [
  { id: "m1", user: "Utente Master · Contabilità", action: "Ha inviato manualmente 8 solleciti fattura questa settimana", suggestion: "Posso automatizzare l'invio dei solleciti sotto i 500€, lasciando a te solo quelli sopra soglia", area: "Contabilità" },
  { id: "m2", user: "Utente Master · Marketing", action: "Ha riutilizzato lo stesso testo promozionale su 3 campagne diverse", suggestion: "Potrei generare varianti automatiche per evitare contenuti duplicati", area: "Marketing" },
  { id: "m3", user: "Utente Master · Posta AI", action: "Ha modificato manualmente la firma email 4 volte nell'ultimo mese", suggestion: "Posso mantenere la firma sincronizzata automaticamente su tutti i moduli PAQ", area: "Comunicazioni" },
];

const LEARNING_REQUESTS = [
  { id: "l1", gap: "Sintesi aggiornata sul Codice del Consumo, sezione pubblicità ingannevole", askedTo: "Claude", status: "Appresa", result: "Integrata in Conoscenza → Diritto privato" },
  { id: "l2", gap: "Interpretazione corretta di fatture emesse in valuta estera", askedTo: "Claude", status: "In corso", result: null },
  { id: "l3", gap: "Differenze di formato data/numero tra le versioni EN e IT della stessa comunicazione", askedTo: "Claude", status: "In corso", result: null },
];
const LEARNING_STATUS_STYLE = {
  "Appresa": { color: C.cyan, bg: C.cyanDim }, "In corso": { color: C.amber, bg: C.amberDim },
};

const RECOGNIZED_IDENTITY = {
  user: "Utente Master", modules: "AREAPAQ · Posta AI", method: "cookie SSO condiviso su .areapaq.it", since: "questa sessione",
};

const CONFLICTS = [
  {
    id: "cf1",
    a: { type: "Funzione", id: "f2", label: "Rispondi confermando ricezione entro 2 ore (Comunicazioni)" },
    b: { type: "Suggerimento", id: "s5", label: "Verifica sempre con il collega prima di rispondere a mail urgenti da Master (Alta)" },
    explanation: "Se un cliente Master scrive una mail urgente, la Funzione impone una risposta automatica entro 2 ore, ma il Suggerimento impone di aspettare la verifica di un collega prima di rispondere: non possono essere applicate entrambe alla lettera.",
    status: "Da risolvere",
  },
];
const CONFLICT_STATUS_STYLE = {
  "Da risolvere": { color: C.rose, bg: C.roseDim }, "Risolto": { color: C.cyan, bg: C.cyanDim },
};

// Stato reale: le 5 aree collegate al portale (stesse di config/areaUrls.js)
// risultano "Connesso" solo se la relativa REACT_APP_*_URL è configurata
// nell'ambiente. Il Portale AREAPAQ è interno, sempre connesso.
const connectedReal = (key) => Boolean(AREA_URLS?.[key]);

const PAQ_INTEGRATIONS = [
  { id: "i1", name: "AreaPAQ", icon: Home, category: "Amministrazione", status: "Connesso", detail: "Accesso diretto a utenti, comunicazioni e impostazioni SMTP" },
  { id: "i2", name: "Posta AI", icon: Mail, category: "Comunicazioni", status: connectedReal("posta") ? "Connesso" : "Da collegare", detail: "Lettura e bozze di risposta sulla casella condivisa" },
  { id: "i3", name: "Risorice", icon: Package, category: "Produzione", status: connectedReal("risorice") ? "Connesso" : "Da collegare", detail: "In attesa di pubblicazione app sugli store" },
  { id: "i4", name: "Paghe", icon: Receipt, category: "Amministrazione", status: connectedReal("paghe") ? "Connesso" : "Da collegare", detail: "Dipendenti, cedolini e presenze collegati al portale" },
  { id: "i5", name: "Contabilità", icon: Receipt, category: "Amministrazione", status: connectedReal("contabilita") ? "Connesso" : "Da collegare", detail: "Webapp collegata al portale" },
  { id: "i6", name: "Tesoreria", icon: Wallet, category: "Amministrazione", status: connectedReal("tesoreria") ? "Connesso" : "Da collegare", detail: "Webapp collegata al portale" },
  { id: "i7", name: "Produzione", icon: Factory, category: "Operativo", status: connectedReal("produzione") ? "Connesso" : "Da collegare", detail: "Webapp collegata al portale" },
];
const INTEGRATION_STYLE = {
  "Connesso": { color: C.cyan, bg: C.cyanDim }, "Da collegare": { color: C.mutedDim, bg: C.surfaceAlt },
};

const MEMORY_BLOCKS = [
  { key: "regole", label: "Regole apprese", icon: BookOpen, count: 6, desc: "Funzioni e suggerimenti diventati comportamento stabile" },
  { key: "clienti", label: "Contesti clienti", icon: Users, count: 0, desc: "Preferenze e storico per cliente, non ancora popolato" },
  { key: "decisioni", label: "Cronologia decisioni", icon: Clock, count: 0, desc: "Traccia delle scelte prese e del relativo esito" },
  { key: "conversazioni", label: "Conversazioni rilevanti", icon: MessageSquare, count: 0, desc: "Scambi salvati come riferimento futuro" },
];

const ACTIVITY_TREND = [
  { ciclo: "Sett. 1", task: 4, precisione: 0.42 },
  { ciclo: "Sett. 2", task: 9, precisione: 0.48 },
  { ciclo: "Sett. 3", task: 15, precisione: 0.53 },
  { ciclo: "Sett. 4", task: 21, precisione: 0.58 },
  { ciclo: "Sett. 5", task: 34, precisione: 0.67 },
];

const AREA_PERF = [
  { area: "Contabilità", precisione: 0.70 },
  { area: "Comunicazioni", precisione: 0.74 },
  { area: "Legale", precisione: 0.28 },
  { area: "Marketing", precisione: 0.44 },
];

/* ================= componenti UI riutilizzabili (identici a Viki_Schedina) ================= */

/* NOTA: nessun componente Sidebar qui — AppShell.js fornisce già il menu
   verticale dell'app (con la voce "V.I.K.I." che porta a questa pagina).
   Un secondo Sidebar interno lo duplicherebbe. */

function PageHeader({ paused, onTogglePause }) {
  return (
    <div className="mb-8 animate-fade-up flex items-start justify-between flex-wrap gap-4">
      <div>
        <p className="font-heading text-xs tracking-[0.35em] uppercase text-white/40 mb-2 flex items-center gap-1.5" style={{ fontWeight: 600 }}>
          <Brain size={13} style={{ color: paused ? C.mutedDim : C.cyan }} /> Intelligenza artificiale
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none"
          style={{ textShadow: paused ? "none" : "0 0 28px rgba(69,224,208,0.35)" }}>V.I.K.I.</h1>
        <p className="text-sm text-slate-400 mt-3 max-w-2xl">
          Il cuore pulsante dell'ecosistema PAQ — sempre affiancata dal tuo controllo.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2" style={{ color: paused ? C.rose : C.cyan, fontSize: 12, ...mono }}>
          <span className="relative flex h-2 w-2">
            {!paused && <span style={{ background: C.cyan }} className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" />}
            <span style={{ background: paused ? C.rose : C.cyan }} className="relative inline-flex rounded-full h-2 w-2" />
          </span>
          {paused ? "IN PAUSA" : "ATTIVO"}
        </div>
        <button onClick={onTogglePause} data-testid="viki-pause-toggle" className="mind-btn flex items-center gap-1.5 px-3 py-1.5 rounded-full"
          style={{
            fontSize: 11, fontWeight: 700, color: paused ? C.cyan : C.rose,
            background: paused ? C.cyanDim : C.roseDim, border: `1px solid ${paused ? C.cyan : C.rose}55`,
          }}>
          <Power size={12} /> {paused ? "Riattiva" : "Blocca VIKI"}
        </button>
      </div>
    </div>
  );
}

// ---------- Guardaroba: anteprima di tutti gli abiti del robottino ----------
const OUTFITS = [
  { key: "base", img: VIKI_IMG.idle, title: "V.I.K.I. classico", where: "Portale AREAPAQ · Risorice", note: "L'abito di tutti i giorni, senza accessori." },
  { key: "finanza", img: VIKI_THEME_IMG.finanza, title: "Contabile", where: "Contabilità · Tesoreria · Paghe", note: "Visiera verde da ragioniere, occhiali dorati e papillon." },
  { key: "postino", img: VIKI_THEME_IMG.postino, title: "Postino", where: "Posta AI", note: "Cappello da postino con stemma e borsa a tracolla con le lettere." },
  { key: "produzione", img: VIKI_THEME_IMG.produzione, title: "Operaio", where: "Produzione", note: "Elmetto giallo da cantiere e pettorina alta visibilità." },
  { key: "helpdesk", img: VIKI_THEME_IMG.helpdesk, title: "Operatore helpdesk", where: "So.Help", note: "Cuffie da call center con microfono, pronto a rispondere ai ticket." },
  { key: "commerciale", img: VIKI_THEME_IMG.commerciale, title: "Agente di commercio", where: "PAQ.WAY", note: "Blazer rosso elegante, cravatta, spilla dorata e ventiquattrore: pronto a chiudere ordini e provvigioni." },
  { key: "natale", img: VIKI_THEME_IMG.natale, title: "Natale", where: "Ovunque · 1 dicembre → 6 gennaio", note: "Cappello di Babbo Natale e sciarpa rossa. Ha priorità sugli abiti delle app.", seasonal: true },
  { key: "estate", img: VIKI_THEME_IMG.estate, title: "Estate", where: "Ovunque · tutto agosto", note: "Cappello di paglia, occhiali da sole e collana di fiori. Ha priorità sugli abiti delle app.", seasonal: true },
];

function GuardarobaTab() {
  const season = seasonalTheme();
  const wornNow = season || "base";
  return (
    <div data-testid="viki-guardaroba-tab">
      <SectionIntro icon={Shirt} title="Il guardaroba di V.I.K.I.">
        Il robottino si veste in base all'app in cui lavora e al periodo dell'anno: nelle festività
        l'abito stagionale ha la precedenza su quello dell'app. Gli abiti compaiono sia nel portale
        che nel widget delle altre app, senza bisogno di aggiornamenti.
      </SectionIntro>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {OUTFITS.map((o, i) => (
          <Card key={o.key} index={i} data-testid={`guardaroba-outfit-${o.key}`} style={{ padding: 18 }}>
            <div className="flex items-center justify-center" style={{
              height: 150, borderRadius: 10, marginBottom: 14,
              background: "radial-gradient(circle at 50% 42%, rgba(69,224,208,0.14) 0%, rgba(10,14,19,0.0) 68%)",
              border: `1px dashed ${o.seasonal ? "rgba(250,204,21,0.35)" : C.border}`,
            }}>
              <img src={o.img} alt={o.title} width={120} height={120} draggable={false}
                style={{ objectFit: "contain", filter: "drop-shadow(0 6px 14px rgba(0,0,0,0.5))" }} />
            </div>
            <div className="flex items-center gap-2">
              <span className="font-heading" style={{ color: C.text, fontSize: 15, fontWeight: 700 }}>{o.title}</span>
              {o.seasonal && (
                <span style={{ color: "#FACC15", background: "rgba(250,204,21,0.12)", border: "1px solid rgba(250,204,21,0.45)",
                  fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99, letterSpacing: 0.4 }}>STAGIONALE</span>
              )}
              {wornNow === o.key && (
                <span data-testid="guardaroba-worn-now" style={{ color: C.cyan, background: C.cyanDim, border: `1px solid ${C.cyan}`,
                  fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99, letterSpacing: 0.4 }}>INDOSSATO ORA</span>
              )}
            </div>
            <div style={{ color: C.cyan, fontSize: 12, fontWeight: 600, marginTop: 5 }}>{o.where}</div>
            <div style={{ color: C.mutedDim, fontSize: 12.5, marginTop: 6, lineHeight: 1.5 }}>{o.note}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------- Home a icone: tutte le funzioni di VIKI in un colpo d'occhio ----------
// Titoli in linguaggio naturale ("Cosa scade presto?") con sotto il nome
// tecnico: l'utente sceglie per intenzione, non per gergo.
const VIKI_FUNCTIONS = [
  { cat: "La tua giornata", color: "#45E0D0", items: [
    { id: "overview", label: "Come va oggi?", sub: "Panoramica", icon: Home, desc: "Lo stato di VIKI e i numeri chiave a colpo d'occhio", kw: "stato numeri panoramica overview" },
    { id: "suggestions", label: "Cosa mi consigli?", sub: "Suggerimenti", icon: Lightbulb, desc: "Le proposte e le idee di VIKI per te", kw: "proposte idee consigli" },
    { id: "deadlines", label: "Cosa scade presto?", sub: "Scadenzario", icon: ClipboardList, desc: "Scadenze fiscali e promemoria in arrivo", kw: "scadenze fiscali promemoria f24 iva" },
    { id: "governance", label: "Cosa devo approvare?", sub: "Governance", icon: ShieldCheck, desc: "Le decisioni in attesa del tuo ok", kw: "decisioni approvazioni permessi" },
  ]},
  { cat: "La salute delle app", color: "#FACC15", items: [
    { id: "dottore", label: "Fai la visita alle app", sub: "V.I.K.I. Dottore", icon: Stethoscope, desc: "Controllo completo dell'ecosistema, rimedi con un clic e diario delle cure", kw: "dottore salute check rimedi playbook cure errori", wide: true },
  ]},
  { cat: "L'ecosistema", color: "#38BDF8", items: [
    { id: "integrations", label: "Le app collegate", sub: "Integrazioni PAQ", icon: Boxes, desc: "Cosa è connesso e cosa manca", kw: "integrazioni collegamenti moduli app sso" },
    { id: "crm", label: "I nostri clienti", sub: "CRM", icon: Users, desc: "Clienti e contatti visti da VIKI", kw: "crm clienti contatti" },
    { id: "meetings", label: "Le riunioni", sub: "Verbali", icon: Calendar, desc: "Appuntamenti e verbali", kw: "riunioni verbali meeting appuntamenti" },
    { id: "centralino", label: "Il centralino", sub: "Chiamate AI", icon: Phone, desc: "Telefonate e centralino intelligente", kw: "centralino telefono chiamate" },
    { id: "staff", label: "Lo staff virtuale", sub: "Team AI", icon: UserCheck, desc: "I collaboratori digitali dell'ecosistema", kw: "staff team virtuale collaboratori" },
  ]},
  { cat: "Dentro VIKI", color: "#A78BFA", items: [
    { id: "functions", label: "Cosa sa fare", sub: "Funzioni", icon: Sparkles, desc: "Tutte le sue capacità attive", kw: "funzioni capacità strumenti" },
    { id: "knowledge", label: "Cosa ha imparato", sub: "Conoscenza", icon: BookOpen, desc: "I domini che conosce e quanto bene", kw: "conoscenza apprendimento domini" },
    { id: "kb", label: "La sua libreria", sub: "Base dati", icon: Database, desc: "Documenti e fonti da cui studia", kw: "base dati libreria documenti kb" },
    { id: "performance", label: "Quanto è brava", sub: "Performance", icon: Activity, desc: "Metriche, precisione e tempi di risposta", kw: "performance metriche velocità" },
    { id: "asset", label: "I suoi lavori", sub: "Asset", icon: Package, desc: "Documenti e materiali che ha creato", kw: "asset documenti materiali generati" },
    { id: "memory", label: "I suoi ricordi", sub: "Memoria", icon: Brain, desc: "Fatti e abitudini che ha memorizzato su di te", kw: "memoria ricordi fatti abitudini" },
    { id: "guardaroba", label: "Il guardaroba", sub: "Abiti del robottino", icon: Shirt, desc: "Come si veste in ogni app e nelle feste", kw: "guardaroba abiti vestiti avatar natale estate" },
    { id: "onboarding", label: "Primi passi", sub: "Onboarding", icon: GraduationCap, desc: "Il suo percorso di attivazione", kw: "onboarding attivazione percorso" },
    { id: "simulation", label: "Zona prove", sub: "Simulazione", icon: FlaskConical, desc: "Prova VIKI in tutta sicurezza", kw: "simulazione prove test sandbox" },
  ]},
];

const ALL_VIKI_FUNCTIONS = VIKI_FUNCTIONS.flatMap((c) => c.items.map((i) => ({ ...i, color: c.color })));

function vikiGreeting(name) {
  const h = new Date().getHours();
  const hi = h < 12 ? "Buongiorno" : h < 18 ? "Buon pomeriggio" : "Buonasera";
  return `${hi}${name ? ` ${name}` : ""}! Sono qui per te: dimmi cosa ti serve, oppure scegli qui sotto.`;
}

function VikiHome({ onOpen }) {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const match = (f) => !needle || `${f.label} ${f.sub} ${f.desc} ${f.kw}`.toLowerCase().includes(needle);
  const visible = VIKI_FUNCTIONS.map((c) => ({ ...c, items: c.items.filter(match) })).filter((c) => c.items.length > 0);
  return (
    <div className="px-4 sm:px-6 py-6" data-testid="viki-home">
      {/* saluto del robottino */}
      <div className="flex items-center gap-4 mb-6 p-4 sm:p-5 rounded-2xl" style={{ background: `radial-gradient(circle at 12% 50%, rgba(69,224,208,0.10) 0%, ${C.surfaceAlt} 65%)`, border: `1px solid ${C.border}` }}>
        <VikiAvatar size={72} mood="wave" theme={seasonalTheme()} float style={{ flexShrink: 0 }} />
        <div className="min-w-0">
          <div className="font-heading" style={{ color: C.text, fontSize: 16.5, fontWeight: 800, letterSpacing: 0.1 }}>
            {vikiGreeting(user?.first_name || user?.nome?.split(" ")[0])}
          </div>
          <div style={{ color: C.mutedDim, fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>
            Ogni riquadro è una cosa che posso fare per te: clicca e ci pensiamo insieme. Per parlarmi, usa il robottino in alto a destra.
          </div>
        </div>
      </div>

      {/* ricerca rapida */}
      <div className="relative mb-7">
        <MessageSquare size={15} style={{ color: C.mutedDim, position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="viki-home-search"
          placeholder="Cerca quello che ti serve… (es. scadenze, guardaroba, clienti)"
          className="w-full viki-home-search"
          style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 12, color: C.text,
            padding: "11px 14px 11px 38px", fontSize: 13.5, outline: "none" }} />
      </div>

      {visible.length === 0 && (
        <div className="text-center py-10" style={{ color: C.mutedDim, fontSize: 13 }}>
          Non ho trovato nulla per “{q}”… prova con un'altra parola, o chiedimelo in chat!
        </div>
      )}

      {visible.map((cat) => (
        <div key={cat.cat} className="mb-7 last:mb-2">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full" style={{ background: cat.color }} />
            <span className="font-heading uppercase" style={{ color: cat.color, fontSize: 11.5, letterSpacing: 1.4, fontWeight: 700 }}>{cat.cat}</span>
            <span className="flex-1 h-px" style={{ background: `${cat.color}22` }} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {cat.items.map((f) => (
              <button key={f.id} onClick={() => onOpen(f.id)} data-testid={`viki-fn-${f.id}`}
                className={`mind-btn text-left p-4 rounded-2xl transition-all group viki-fn-card ${f.wide ? "col-span-2 sm:col-span-3 lg:col-span-4 sm:flex sm:items-center sm:gap-4" : ""}`}
                style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = cat.color; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.transform = "none"; }}>
                <span className={`flex items-center justify-center rounded-xl ${f.wide ? "sm:mb-0 mb-3" : "mb-3"}`} style={{
                  width: 42, height: 42, background: `${cat.color}1A`, border: `1px solid ${cat.color}55`, flexShrink: 0,
                }}>
                  <f.icon size={21} style={{ color: cat.color }} />
                </span>
                <span className="block min-w-0">
                  <span className="font-heading block" style={{ color: C.text, fontSize: 14.5, fontWeight: 700, letterSpacing: 0.1 }}>{f.label}</span>
                  <span className="block uppercase" style={{ color: cat.color, fontSize: 9.5, letterSpacing: 1, fontWeight: 700, marginTop: 3 }}>{f.sub}</span>
                  <span className="block" style={{ color: C.mutedDim, fontSize: 11.5, marginTop: 4, lineHeight: 1.45 }}>{f.desc}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FunctionHeader({ fn, onBack }) {
  return (
    <div className="flex items-center gap-3 px-4 sm:px-6 pt-4 pb-3.5" style={{ borderBottom: `1px solid ${C.border}` }}>
      <button onClick={onBack} data-testid="viki-back-home"
        className="mind-btn font-heading flex items-center gap-1.5 px-3.5 py-2 rounded-full whitespace-nowrap"
        style={{ color: C.bg, background: C.cyan, border: `1px solid ${C.cyan}`, fontSize: 12.5, fontWeight: 700 }}>
        <ArrowLeft size={13} /> Tutte le funzioni
      </button>
      {fn && (
        <span className="flex items-center gap-2 min-w-0">
          <fn.icon size={15} style={{ color: fn.color, flexShrink: 0 }} />
          <span className="font-heading truncate" style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>{fn.label}</span>
          <span className="hidden sm:inline truncate" style={{ color: C.mutedDim, fontSize: 12 }}>— {fn.desc}</span>
        </span>
      )}
    </div>
  );
}

const TAB_GROUPS = [
  { id: "overview", label: "Panoramica", icon: Home, tabs: [
    { id: "overview", label: "Panoramica" },
  ]},
  { id: "operativo", label: "Operatività", icon: Sparkles, tabs: [
    { id: "functions", label: "Funzioni" },
    { id: "suggestions", label: "Suggerimenti" },
    { id: "deadlines", label: "Scadenzario" },
    { id: "governance", label: "Governance" },
  ]},
  { id: "ecosistema", label: "Ecosistema", icon: Boxes, tabs: [
    { id: "integrations", label: "Integrazioni PAQ" },
    { id: "crm", label: "CRM" },
    { id: "meetings", label: "Riunioni" },
    { id: "centralino", label: "Centralino" },
    { id: "staff", label: "Staff Virtuale" },
  ]},
  { id: "crescita", label: "Crescita", icon: TrendingUp, tabs: [
    { id: "knowledge", label: "Conoscenza" },
    { id: "kb", label: "Base dati" },
    { id: "performance", label: "Performance" },
    { id: "asset", label: "Asset" },
  ]},
  { id: "sistema", label: "Sistema", icon: Settings, tabs: [
    { id: "memory", label: "Memoria" },
    { id: "guardaroba", label: "Guardaroba" },
    { id: "onboarding", label: "Onboarding" },
    { id: "simulation", label: "Simulazione" },
  ]},
  { id: "dottore", label: "V.I.K.I. Dottore", icon: Stethoscope, yellow: true, tabs: [
    { id: "dottore", label: "V.I.K.I. Dottore" },
  ]},
];

function GroupNav({ activeGroupId, onSelectGroup }) {
  return (
    <div className="flex gap-1.5 px-6 pt-4 pb-3 overflow-x-auto mind-scrollbar">
      {TAB_GROUPS.map((g) => {
        const active = activeGroupId === g.id;
        const yl = "#FACC15";
        return (
          <button key={g.id} onClick={() => onSelectGroup(g.id)} className="font-heading mind-btn flex items-center gap-1.5 px-3.5 py-2 rounded-full whitespace-nowrap"
            data-testid={`viki-group-${g.id}`}
            style={{
              color: active ? C.bg : (g.yellow ? yl : C.muted),
              background: active ? (g.yellow ? yl : C.cyan) : (g.yellow ? "rgba(250,204,21,0.12)" : C.surfaceAlt),
              border: `1px solid ${active ? (g.yellow ? yl : C.cyan) : (g.yellow ? "rgba(250,204,21,0.55)" : C.border)}`, fontSize: 12.5, fontWeight: 700, letterSpacing: 0.2,
            }}>
            <g.icon size={13} /> {g.label}
          </button>
        );
      })}
    </div>
  );
}

function SubTabNav({ group, tab, setTab }) {
  if (!group || group.tabs.length <= 1) return <div style={{ borderBottom: `1px solid ${C.border}` }} />;
  return (
    <div className="flex gap-1 px-6 overflow-x-auto mind-scrollbar" style={{ borderBottom: `1px solid ${C.border}` }}>
      {group.tabs.map((t) => (
        <button key={t.id} onClick={() => setTab(t.id)} className="font-heading px-3.5 py-2.5 text-[13px] transition-colors whitespace-nowrap"
          style={{ color: tab === t.id ? C.text : C.mutedDim, borderBottom: tab === t.id ? `2px solid ${C.cyan}` : "2px solid transparent", fontWeight: tab === t.id ? 600 : 500 }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Card({ children, style, index = 0, ...rest }) {
  return <div className="mind-card" style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, animationDelay: `${index * 0.06}s`, ...style }} {...rest}>{children}</div>;
}

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card style={{ padding: 16, flex: 1 }}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-heading uppercase" style={{ color: C.mutedDim, fontSize: 11, letterSpacing: 0.6, fontWeight: 600 }}>{label}</span>
        <Icon size={15} style={{ color: accent || C.muted }} />
      </div>
      <div style={{ color: C.text, fontSize: 24, fontWeight: 600, ...mono }}>{value}</div>
      {sub && <div style={{ color: C.mutedDim, fontSize: 12, marginTop: 4 }}>{sub}</div>}
    </Card>
  );
}

function SectionIntro({ icon: Icon, title, children }) {
  return (
    <div className="flex items-start gap-2 px-1" style={{ color: C.mutedDim, fontSize: 12, maxWidth: 760 }}>
      <Icon size={14} style={{ marginTop: 1, flexShrink: 0, color: C.cyan }} />
      <span><b className="font-heading" style={{ color: C.text, fontWeight: 700, letterSpacing: 0.1 }}>{title}</b> — {children}</span>
    </div>
  );
}

/* ---------- anteprima reale di un template newsletter generato da VIKI,
   con la stessa identità visiva della Guida di stile PAQ ---------- */
function NewsletterTemplatePreview() {
  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", maxWidth: 360 }}>
      <div className="flex items-center gap-2 px-4 py-3" style={{ background: C.surfaceAlt, borderBottom: `1px solid ${C.border}` }}>
        <Brain size={14} style={{ color: C.cyan }} />
        <span style={{ color: C.text, fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>PAQ</span>
      </div>
      <div style={{ padding: "22px 18px", background: `radial-gradient(circle at 50% 0%, #0F2A28 0%, ${C.bg} 70%)` }}>
        <div style={{ color: C.cyan, fontSize: 9.5, letterSpacing: 1.5, textTransform: "uppercase", ...mono, marginBottom: 6 }}>Newsletter · Fidelizzazione</div>
        <div style={{ color: C.text, fontSize: 17, fontWeight: 800, lineHeight: 1.3, marginBottom: 8 }}>Le novità dell'ecosistema PAQ, solo per te</div>
        <div style={{ color: C.muted, fontSize: 11.5, lineHeight: 1.6, marginBottom: 14 }}>
          Un aggiornamento pensato per i nostri clienti più fedeli: cosa è cambiato, cosa arriva presto, e un vantaggio riservato a chi legge fino in fondo.
        </div>
        <div style={{ display: "inline-block", background: C.cyan, color: C.bg, fontSize: 11.5, fontWeight: 700, padding: "9px 18px", borderRadius: 7 }}>
          Scopri le novità
        </div>
      </div>
      <div className="px-4 py-2.5" style={{ borderTop: `1px solid ${C.border}`, background: C.surfaceAlt }}>
        <span style={{ color: C.mutedDim, fontSize: 9.5 }}>Generato da VIKI · palette e tipografia coerenti con la Guida di stile PAQ</span>
      </div>
    </div>
  );
}

/* ================= Overview ================= */
function FriendlyReveal({ title, hint, icon: Icon, children, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card style={{ padding: 0, overflow: "hidden" }} data-testid={testId}>
      <button onClick={() => setOpen((o) => !o)} className="mind-btn w-full flex items-center justify-between px-5 py-4 text-left" style={{ background: "transparent" }}>
        <span className="flex items-center gap-2.5 min-w-0">
          <span className="flex items-center justify-center rounded-lg shrink-0" style={{ width: 32, height: 32, background: C.cyanDim, border: `1px solid ${C.cyan}44` }}>
            <Icon size={15} style={{ color: C.cyan }} />
          </span>
          <span className="min-w-0">
            <span className="font-heading block truncate" style={{ color: C.text, fontSize: 13.5, fontWeight: 700 }}>{title}</span>
            {hint && <span className="block truncate" style={{ color: C.mutedDim, fontSize: 11 }}>{hint}</span>}
          </span>
        </span>
        <ChevronRight size={16} style={{ color: C.mutedDim, flexShrink: 0, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.2s ease" }} />
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </Card>
  );
}

function FriendlyStat({ tone = "ok", value, label, sub, testId }) {
  const dot = tone === "ok" ? "#34D399" : tone === "warn" ? "#FACC15" : "#F87171";
  return (
    <Card style={{ padding: 18, flex: 1, minWidth: 150 }} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: dot, boxShadow: `0 0 8px ${dot}88` }} />
        <span className="font-heading uppercase" style={{ color: C.mutedDim, fontSize: 10.5, letterSpacing: 0.8, fontWeight: 700 }}>{label}</span>
      </div>
      <div style={{ color: C.text, fontSize: 26, fontWeight: 700, ...mono }}>{value}</div>
      <div style={{ color: C.mutedDim, fontSize: 11.5, marginTop: 4, lineHeight: 1.45 }}>{sub}</div>
    </Card>
  );
}

function OverviewTab() {
  const { items: fns } = usePersistedList("viki-areapaq-functions", FUNCTIONS);
  const { items: sugg } = usePersistedList("viki-areapaq-suggestions", SUGGESTIONS);
  const integr = PAQ_INTEGRATIONS;

  const fnLabel = fns ? `${fns.filter((f) => f.status === "Appresa").length} / ${fns.length}` : "—";
  const suggLabel = sugg ? `${sugg.filter((s) => s.active).length} / ${sugg.length}` : "—";
  const integrLabel = integr ? `${integr.filter((i) => i.status === "Connesso").length} / ${integr.length}` : "—";

  return (
    <div className="flex flex-col gap-5">
      <div style={{ position: "relative", overflow: "hidden", background: `radial-gradient(circle at 50% 38%, #0F2A28 0%, ${C.bg} 62%)`, borderRadius: 12, border: `1px solid ${C.border}`, padding: "32px 24px 28px" }}>
        <GrowthIndicator />
      </div>

      <div className="flex flex-col gap-5 pb-6">
        <div className="flex gap-4 flex-wrap md:flex-nowrap">
          <StatCard icon={Sparkles} label="Funzioni apprese" value={fnLabel} sub="istruzioni tradotte in comportamento" accent={C.cyan} />
          <StatCard icon={Lightbulb} label="Suggerimenti attivi" value={suggLabel} sub="correzioni in vigore" accent={C.amber} />
          <StatCard icon={Link2} label="Moduli PAQ connessi" value={integrLabel} sub="ecosistema collegato" accent={C.violet} />
        </div>

        <RadarStrip items={integr} />
        <HabitsCard />
        <MemoryCard />
        <GrowthTimeline />

        <Card style={{ padding: 20 }}>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={15} style={{ color: C.cyan }} />
            <span style={{ color: C.text, fontSize: 13.5, fontWeight: 600 }}>Come cresce VIKI</span>
          </div>
          <p style={{ color: C.mutedDim, fontSize: 11.5, marginBottom: 14, lineHeight: 1.5 }}>
            Non un trucco unico: gli stessi quattro leve con cui si fa evolvere un modello linguistico, applicate qui in modo concreto.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex gap-2.5 items-start p-3 rounded-md" style={{ background: C.surfaceAlt }}>
              <BookOpen size={14} style={{ color: C.cyan, marginTop: 2, flexShrink: 0 }} />
              <div>
                <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>Dati di qualità</div>
                <span style={{ color: C.muted, fontSize: 11.5, lineHeight: 1.5 }}>In <b style={{ color: C.text }}>Conoscenza</b> più informazioni pulite ed esempi reali entrano nei domini attivi, più aumenta il livello di padronanza.</span>
              </div>
            </div>
            <div className="flex gap-2.5 items-start p-3 rounded-md" style={{ background: C.surfaceAlt }}>
              <Sparkles size={14} style={{ color: C.violet, marginTop: 2, flexShrink: 0 }} />
              <div>
                <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>Dimensione &amp; capacità</div>
                <span style={{ color: C.muted, fontSize: 11.5, lineHeight: 1.5 }}>Ogni nuova <b style={{ color: C.text }}>Funzione</b> insegnata amplia ciò che VIKI sa gestire da sola, senza intervento umano ripetuto.</span>
              </div>
            </div>
            <div className="flex gap-2.5 items-start p-3 rounded-md" style={{ background: C.surfaceAlt }}>
              <Lightbulb size={14} style={{ color: C.amber, marginTop: 2, flexShrink: 0 }} />
              <div>
                <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>Correzione umana (RLHF)</div>
                <span style={{ color: C.muted, fontSize: 11.5, lineHeight: 1.5 }}>I <b style={{ color: C.text }}>Suggerimenti</b> sono il tuo feedback diretto: correggi un comportamento sbagliato e VIKI impara cosa è giusto.</span>
              </div>
            </div>
            <div className="flex gap-2.5 items-start p-3 rounded-md" style={{ background: C.surfaceAlt }}>
              <Link2 size={14} style={{ color: C.rose, marginTop: 2, flexShrink: 0 }} />
              <div>
                <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>Ragionamento &amp; fonti esterne</div>
                <span style={{ color: C.muted, fontSize: 11.5, lineHeight: 1.5 }}>Le <b style={{ color: C.text }}>Integrazioni PAQ</b> collegano VIKI ai dati reali dell'ecosistema invece di farla ragionare solo a memoria.</span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ================= Funzioni ================= */
function FunctionsTab() {
  const { user } = useAuth();
  const isEditor = user?.role === "master";
  const { items, persist, loading } = usePersistedList("viki-areapaq-functions", FUNCTIONS);
  const [draft, setDraft] = useState("");
  const [category, setCategory] = useState("Generale");
  const [openSandbox, setOpenSandbox] = useState({});
  const [dismissedPromotions, setDismissedPromotions] = useState({});
  const PROMOTION_THRESHOLD = 10;

  const addFunction = () => {
    if (!isEditor || !draft.trim()) return;
    persist((cur) => [{ id: `f${Date.now()}`, text: draft.trim(), category, status: "Da verificare", applied: 0 }, ...(cur || [])]);
    setDraft("");
  };
  const removeFunction = (id) => { if (isEditor) persist((cur) => (cur || []).filter((f) => f.id !== id)); };
  const toggleSandbox = (id) => setOpenSandbox((cur) => ({ ...cur, [id]: !cur[id] }));
  const promoteFunction = (id) => { if (isEditor) persist((cur) => (cur || []).map((f) => (f.id === id ? { ...f, status: "Appresa" } : f))); };
  const dismissPromotion = (id) => setDismissedPromotions((cur) => ({ ...cur, [id]: true }));

  return (
    <div className="p-6 flex flex-col gap-4">
      <SectionIntro icon={Info} title="Funzioni">
        scrivi una frase in linguaggio naturale che descrive cosa VIKI deve fare in una determinata situazione. Diventa una regola operativa che applica da sola.
      </SectionIntro>

      {!isEditor && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}>
          <Info size={13} style={{ color: C.mutedDim, flexShrink: 0 }} />
          <span style={{ color: C.mutedDim, fontSize: 11.5 }}>Puoi vedere le Funzioni insegnate a VIKI, ma solo il ruolo Master può aggiungerle, modificarle o rimuoverle.</span>
        </div>
      )}

      {isEditor && (
        <Card style={{ padding: 16 }}>
          <div className="flex flex-col gap-2">
            <textarea className="mind-input rounded-md p-3 text-sm" rows={2} placeholder='Es. "Quando un cliente chiede uno sconto oltre il 10%, chiedi sempre conferma prima di rispondere"'
              style={{ resize: "vertical", fontSize: 12.5 }} value={draft} onChange={(e) => setDraft(e.target.value)} />
            <div className="flex items-center gap-2 justify-between flex-wrap">
              <select className="mind-input rounded-md px-3 py-2 text-xs" value={category} onChange={(e) => setCategory(e.target.value)}>
                {Object.keys(FUNCTION_CATEGORIES).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button onClick={addFunction} className="mind-btn flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-semibold" style={{ background: C.cyanDim, color: C.cyan, border: `1px solid ${C.cyan}55` }}>
                <Plus size={13} /> Insegna a VIKI
              </button>
            </div>
          </div>
        </Card>
      )}

      {loading ? <LoadingRow /> : (
        <div className="flex flex-col gap-2.5">
          {(items || []).filter((f) => !(f.text || "").includes("WhatsApp")).map((f, i) => {
            const Icon = FUNCTION_CATEGORIES[f.category] || Sparkles;
            const st = FUNCTION_STATUS_STYLE[f.status];
            return (
              <Card key={f.id} index={i} style={{ padding: 14 }}>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center shrink-0" style={{ width: 30, height: 30, borderRadius: 8, background: C.surfaceAlt, color: C.muted }}>
                    <Icon size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div style={{ color: C.text, fontSize: 13, lineHeight: 1.5 }}>{f.text}</div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span style={{ color: C.mutedDim, fontSize: 10.5, ...mono, background: C.surfaceAlt, padding: "2px 7px", borderRadius: 4 }}>{f.category}</span>
                      <span style={{ color: st.color, fontSize: 10.5, background: st.bg, padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>{f.status}</span>
                      {f.applied > 0 && <span style={{ color: C.mutedDim, fontSize: 10.5 }}>applicata {f.applied} volte</span>}
                    </div>
                    {f.reason && (
                      <div className="flex items-start gap-1.5 mt-2 pt-2" style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                        <Brain size={11} style={{ color: C.violet, marginTop: 2, flexShrink: 0 }} />
                        <span style={{ color: C.mutedDim, fontSize: 11, lineHeight: 1.5 }}><b style={{ color: C.muted, fontWeight: 600 }}>Perché:</b> {f.reason}</span>
                      </div>
                    )}
                    {f.source && (
                      <div className="flex items-start gap-1.5 mt-1.5">
                        <Link2 size={11} style={{ color: C.mutedDim, marginTop: 2, flexShrink: 0 }} />
                        <span style={{ color: C.mutedDim, fontSize: 11, lineHeight: 1.5 }}><b style={{ color: C.muted, fontWeight: 600 }}>Fonte:</b> {f.source}</span>
                      </div>
                    )}
                    {isEditor && f.status === "In apprendimento" && f.applied >= PROMOTION_THRESHOLD && !dismissedPromotions[f.id] && (
                      <div className="mt-2.5 p-2.5 rounded-md" style={{ background: C.cyanDim, border: `1px solid ${C.cyan}44` }}>
                        <div className="flex items-start gap-1.5 mb-2">
                          <ArrowUpRight size={12} style={{ color: C.cyan, marginTop: 2, flexShrink: 0 }} />
                          <span style={{ color: C.text, fontSize: 11.5, lineHeight: 1.5 }}>VIKI propone di promuovere questa Funzione a <b>"Appresa"</b>: {f.applied} applicazioni senza correzioni.</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => promoteFunction(f.id)} className="mind-btn px-3 py-1.5 rounded-md" style={{ background: C.cyan, color: C.bg, fontSize: 11, fontWeight: 700 }}>Promuovi</button>
                          <button onClick={() => dismissPromotion(f.id)} className="mind-btn px-3 py-1.5 rounded-md" style={{ background: "transparent", color: C.muted, fontSize: 11, fontWeight: 600, border: `1px solid ${C.border}` }}>Non ancora</button>
                        </div>
                      </div>
                    )}
                    {SANDBOX_CASES[f.id] && (
                      <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                        <button onClick={() => toggleSandbox(f.id)} className="mind-btn flex items-center gap-1.5" style={{ color: C.violet, fontSize: 11, fontWeight: 600 }}>
                          <Zap size={11} /> {openSandbox[f.id] ? "Nascondi anteprima" : (f.status === "Appresa" ? "Vedi un esempio d'uso" : "Prova su un caso reale")}
                        </button>
                        {openSandbox[f.id] && (() => {
                          const sb = SANDBOX_CASES[f.id];
                          const vst = SANDBOX_VERDICT_STYLE[sb.verdict];
                          return (
                            <div className="mt-2 p-2.5 rounded-md" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}>
                              <div style={{ color: C.mutedDim, fontSize: 10.5, marginBottom: 4 }}>{f.status === "Appresa" ? "Caso d'uso tipico:" : "Caso reale passato:"}</div>
                              <div style={{ color: C.text, fontSize: 12, lineHeight: 1.5, marginBottom: 8 }}>{sb.caseText}</div>
                              {sb.visual === "newsletter" ? (
                                <div className="mb-2">
                                  <NewsletterTemplatePreview />
                                </div>
                              ) : (
                                <>
                                  <div style={{ color: C.mutedDim, fontSize: 10.5, marginBottom: 4 }}>Cosa farebbe VIKI:</div>
                                  <div style={{ color: C.text, fontSize: 12, lineHeight: 1.5, marginBottom: 8 }}>{sb.result}</div>
                                </>
                              )}
                              <span style={{ color: vst.color, fontSize: 10.5, background: vst.bg, padding: "2px 9px", borderRadius: 20, fontWeight: 600 }}>{sb.verdict}</span>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  {isEditor && <button onClick={() => removeFunction(f.id)} className="mind-btn shrink-0" style={{ color: C.mutedDim }}><Trash2 size={14} /></button>}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ================= Base dati (RAG) ================= */
function KBTab() {
  const { user } = useAuth();
  const isEditor = user?.role === "master";
  const [documents, setDocuments] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [source, setSource] = useState("");
  const [category, setCategory] = useState("Generale");
  const [status, setStatus] = useState("idle"); // idle | saving | error
  const [errorMsg, setErrorMsg] = useState("");

  const loadDocuments = () => {
    api.get("/viki/kb/list")
      .then(({ data }) => setDocuments(data.documents || []))
      .catch(() => setDocuments([]));
  };
  useEffect(() => { loadDocuments(); }, []);

  const addDocument = async () => {
    if (!isEditor || !title.trim() || !content.trim()) return;
    setStatus("saving");
    setErrorMsg("");
    try {
      await api.post("/viki/kb/ingest", {
        title: title.trim(), content: content.trim(),
        source: source.trim() || undefined, category: category.trim() || "Generale",
      });
      setTitle(""); setContent(""); setSource("");
      setStatus("idle");
      loadDocuments();
    } catch (e) {
      setStatus("error");
      setErrorMsg(e?.response?.data?.detail || "Non sono riuscito ad aggiungere il documento. Riprova.");
    }
  };

  const removeDocument = async (docId) => {
    if (!isEditor) return;
    try {
      await api.delete(`/viki/kb/${docId}`);
      loadDocuments();
    } catch { /* l'elenco resta quello di prima, l'utente può riprovare */ }
  };

  return (
    <div className="p-6 flex flex-col gap-4">
      <SectionIntro icon={Database} title="Base dati">
        documenti scelti a mano che VIKI può consultare per rispondere — non presi in automatico da
        internet: sei tu a decidere cosa entra qui. VIKI li cerca per significato, non per parola esatta.
      </SectionIntro>

      {!isEditor && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}>
          <Info size={13} style={{ color: C.mutedDim, flexShrink: 0 }} />
          <span style={{ color: C.mutedDim, fontSize: 11.5 }}>Puoi vedere i documenti già caricati, ma solo il ruolo Master può aggiungerne o rimuoverne.</span>
        </div>
      )}

      {isEditor && (
        <Card style={{ padding: 16 }}>
          <div className="flex flex-col gap-2">
            <input className="mind-input rounded-md px-3 py-2 text-sm" placeholder="Titolo del documento"
              value={title} onChange={(e) => setTitle(e.target.value)} />
            <textarea className="mind-input rounded-md p-3 text-sm" rows={5} placeholder="Incolla qui il contenuto — un testo, una procedura, un estratto di documentazione..."
              style={{ resize: "vertical", fontSize: 12.5 }} value={content} onChange={(e) => setContent(e.target.value)} />
            <div className="flex items-center gap-2 flex-wrap">
              <input className="mind-input rounded-md px-3 py-2 text-xs flex-1" placeholder='Fonte (es. "Hugging Face — nome dataset", "Manuale interno")'
                value={source} onChange={(e) => setSource(e.target.value)} />
              <input className="mind-input rounded-md px-3 py-2 text-xs" style={{ width: 160 }} placeholder="Categoria"
                value={category} onChange={(e) => setCategory(e.target.value)} />
            </div>
            <div className="flex items-center gap-2 justify-between">
              {status === "error" && <span style={{ color: "#E0616F", fontSize: 11 }}>{errorMsg}</span>}
              <button onClick={addDocument} disabled={status === "saving"} className="mind-btn flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-semibold ml-auto"
                style={{ background: C.cyanDim, color: C.cyan, border: `1px solid ${C.cyan}55` }}>
                <Plus size={13} /> {status === "saving" ? "Aggiungo…" : "Aggiungi alla base dati"}
              </button>
            </div>
          </div>
        </Card>
      )}

      {documents === null ? (
        <LoadingRow />
      ) : documents.length === 0 ? (
        <div style={{ color: C.mutedDim, fontSize: 11.5, lineHeight: 1.5, padding: "8px 2px" }}>
          Ancora nessun documento caricato.
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          {documents.map((d, i) => (
            <Card key={d.doc_id} index={i} style={{ padding: 14 }}>
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center shrink-0" style={{ width: 30, height: 30, borderRadius: 8, background: C.surfaceAlt, color: C.muted }}>
                  <Database size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div style={{ color: C.text, fontSize: 13, fontWeight: 600, lineHeight: 1.5 }}>{d.title}</div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span style={{ color: C.mutedDim, fontSize: 10.5, ...mono, background: C.surfaceAlt, padding: "2px 7px", borderRadius: 4 }}>{d.category}</span>
                    <span style={{ color: C.mutedDim, fontSize: 10.5 }}>{d.chunks} {d.chunks === 1 ? "blocco" : "blocchi"}</span>
                  </div>
                  <div className="flex items-start gap-1.5 mt-2 pt-2" style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                    <Link2 size={11} style={{ color: C.mutedDim, marginTop: 2, flexShrink: 0 }} />
                    <span style={{ color: C.mutedDim, fontSize: 11, lineHeight: 1.5 }}><b style={{ color: C.muted, fontWeight: 600 }}>Fonte:</b> {d.source}</span>
                  </div>
                </div>
                {isEditor && <button onClick={() => removeDocument(d.doc_id)} className="mind-btn shrink-0" style={{ color: C.mutedDim }}><Trash2 size={14} /></button>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ================= Suggerimenti ================= */
function SuggestionsTab() {
  const { user } = useAuth();
  const isEditor = user?.role === "master";
  const { items, persist, loading } = usePersistedList("viki-areapaq-suggestions", SUGGESTIONS);
  const [draft, setDraft] = useState("");
  const [priority, setPriority] = useState("Media");

  const addSuggestion = () => {
    if (!isEditor || !draft.trim()) return;
    persist((cur) => [{ id: `s${Date.now()}`, text: draft.trim(), priority, active: true }, ...(cur || [])]);
    setDraft("");
  };
  const toggleActive = (id) => { if (isEditor) persist((cur) => (cur || []).map((s) => (s.id === id ? { ...s, active: !s.active } : s))); };
  const removeSuggestion = (id) => { if (isEditor) persist((cur) => (cur || []).filter((s) => s.id !== id)); };

  return (
    <div className="p-6 flex flex-col gap-4">
      <SectionIntro icon={Info} title="Suggerimenti">
        correggi un comportamento specifico: "questa cosa invece di farla così, sarebbe meglio che tu la facessi così".
      </SectionIntro>

      {!isEditor && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}>
          <Info size={13} style={{ color: C.mutedDim, flexShrink: 0 }} />
          <span style={{ color: C.mutedDim, fontSize: 11.5 }}>Puoi vedere i Suggerimenti attivi, ma solo il ruolo Master può aggiungerli, attivarli/disattivarli o rimuoverli.</span>
        </div>
      )}

      {isEditor && (
        <Card style={{ padding: 16 }}>
          <div className="flex flex-col gap-2">
            <textarea className="mind-input rounded-md p-3 text-sm" rows={2} placeholder='Es. "Invece di rispondere subito, aspetta che il cliente confermi la data prima di generare il preventivo"'
              style={{ resize: "vertical", fontSize: 12.5 }} value={draft} onChange={(e) => setDraft(e.target.value)} />
            <div className="flex items-center gap-2 justify-between flex-wrap">
              <select className="mind-input rounded-md px-3 py-2 text-xs" value={priority} onChange={(e) => setPriority(e.target.value)}>
                <option value="Alta">Priorità Alta</option><option value="Media">Priorità Media</option><option value="Bassa">Priorità Bassa</option>
              </select>
              <button onClick={addSuggestion} className="mind-btn flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-semibold" style={{ background: C.amberDim, color: C.amber, border: `1px solid ${C.amber}55` }}>
                <Lightbulb size={13} /> Aggiungi suggerimento
              </button>
            </div>
          </div>
        </Card>
      )}

      {loading ? <LoadingRow /> : (
        <div className="flex flex-col gap-2.5">
          {(items || []).map((s, i) => {
            const st = PRIORITY_STYLE[s.priority];
            return (
              <Card key={s.id} index={i} style={{ padding: 14, opacity: s.active ? 1 : 0.55 }}>
                <div className="flex items-center gap-3">
                  <button onClick={() => toggleActive(s.id)} className="shrink-0" disabled={!isEditor} style={{ color: s.active ? C.cyan : C.mutedDim, cursor: isEditor ? "pointer" : "default" }}>
                    {s.active ? <CheckCircle2 size={17} /> : <Power size={17} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div style={{ color: C.text, fontSize: 13, lineHeight: 1.5 }}>{s.text}</div>
                    <span style={{ color: st.color, fontSize: 10.5, background: st.bg, padding: "2px 8px", borderRadius: 20, fontWeight: 600, marginTop: 6, display: "inline-block" }}>{s.priority}</span>
                    {s.reason && (
                      <div className="flex items-start gap-1.5 mt-2 pt-2" style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                        <Brain size={11} style={{ color: C.violet, marginTop: 2, flexShrink: 0 }} />
                        <span style={{ color: C.mutedDim, fontSize: 11, lineHeight: 1.5 }}><b style={{ color: C.muted, fontWeight: 600 }}>Perché:</b> {s.reason}</span>
                      </div>
                    )}
                  </div>
                  {isEditor && <button onClick={() => removeSuggestion(s.id)} className="mind-btn shrink-0" style={{ color: C.mutedDim }}><Trash2 size={14} /></button>}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ================= Conoscenza ================= */
const MARKETING_SKILLS = [
  { key: "seo", label: "SEO", desc: "Ottimizzazione per comparire nei risultati organici di Google", value: 0.15 },
  { key: "sem", label: "SEM & Advertising (PPC)", desc: "Annunci a pagamento su motori di ricerca e social", value: 0.12 },
  { key: "content", label: "Content Marketing", desc: "Articoli, video e podcast di valore per il pubblico", value: 0.22 },
  { key: "social", label: "Social Media Marketing", desc: "Gestione dei canali social, community e interazione", value: 0.18 },
  { key: "email", label: "Email Marketing", desc: "Messaggi diretti per fidelizzare i contatti (newsletter)", value: 0.26 },
  { key: "inbound", label: "Inbound Marketing", desc: "Attrarre e convertire i clienti con contenuti su misura", value: 0.14 },
];

/* ---------- accordion per le Aree di competenza: macro-area espandibile,
   con sotto-materie o sotto-gruppi annidati (Diritto Pubblico/Privato). ---------- */
function MiniBar({ label, desc, value }) {
  return (
    <div className="py-2">
      <div className="flex items-center justify-between mb-1">
        <span style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{label}</span>
        <span style={{ color: C.cyan, fontSize: 11, ...mono }}>{Math.round(value * 100)}%</span>
      </div>
      <div style={{ height: 5, background: C.surfaceAlt, borderRadius: 3, overflow: "hidden", marginBottom: desc ? 3 : 0 }}>
        <div style={{ width: `${value * 100}%`, height: "100%", background: C.cyan }} />
      </div>
      {desc && <div style={{ color: C.mutedDim, fontSize: 10.5, lineHeight: 1.4 }}>{desc}</div>}
    </div>
  );
}

function KnowledgeAreaAccordion() {
  const [open, setOpen] = useState({});
  const toggle = (key) => setOpen((cur) => ({ ...cur, [key]: !cur[key] }));

  return (
    <div className="flex flex-col gap-2.5">
      {KNOWLEDGE_AREAS.map((area) => {
        const avg = areaAverage(area);
        const isOpen = !!open[area.key];
        return (
          <Card key={area.key} style={{ padding: 0, overflow: "hidden" }}>
            <button onClick={() => toggle(area.key)} className="w-full flex items-center gap-3 p-4 text-left" style={{ background: "transparent" }}>
              <div className="flex items-center justify-center shrink-0" style={{ width: 32, height: 32, borderRadius: 8, background: C.cyanDim, color: C.cyan }}>
                <area.icon size={15} />
              </div>
              <div className="flex-1 min-w-0">
                <span style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{area.label}</span>
              </div>
              <span style={{ color: C.cyan, fontSize: 11.5, ...mono }}>{Math.round(avg * 100)}%</span>
              <ChevronRight size={16} style={{ color: C.mutedDim, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.2s" }} />
            </button>
            {isOpen && (
              <div className="px-4 pb-4" style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                {area.single && (
                  <div className="pt-2">
                    <MiniBar label={area.label} desc={area.single.desc} value={area.single.value} />
                  </div>
                )}
                {area.items && (
                  <div className="divide-y" style={{ borderColor: C.borderSoft }}>
                    {area.items.map((it) => <MiniBar key={it.label} {...it} />)}
                  </div>
                )}
                {area.groups && area.groups.map((g) => (
                  <div key={g.key} className="pt-2">
                    <div className="flex items-center gap-1.5" style={{ color: C.mutedDim, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>
                      {g.icon && <g.icon size={12} style={{ color: C.cyan }} />}
                      {g.label}
                    </div>
                    <div className="divide-y" style={{ borderColor: C.borderSoft }}>
                      {g.items.map((it) => <MiniBar key={it.label} {...it} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function KnowledgeTab() {
  return (
    <div className="p-6 flex flex-col gap-4">
      <SectionIntro icon={Info} title="Conoscenza">
        i domini che VIKI deve padroneggiare per diventare il cuore pulsante dell'ecosistema PAQ.
      </SectionIntro>
      <div className="flex items-start gap-2 px-1 mb-1" style={{ color: C.mutedDim, fontSize: 11, maxWidth: 720 }}>
        <Brain size={13} style={{ marginTop: 1, flexShrink: 0, color: C.violet }} />
        <span>Valori calibrati sulla capacità reale del modello che alimenta VIKI (classe GPT-5.4), su benchmark pubblici come MMLU-Pro: le materie di conoscenza e comunicazione (lingua, matematica, scrittura) sono più vicine al tetto attuale dei modelli migliori (83-90%); diritto ed economia stanno nel mezzo, come nei dati reali. Resta comunque sapere teorico, non affidabilità operativa provata sul campo — quella la trovi in ogni Funzione, con applicazioni ed esiti reali.</span>
      </div>

      <div>
        <div className="flex items-center gap-2 px-1 mb-2">
          <GraduationCap size={15} style={{ color: C.cyan }} />
          <span style={{ color: C.text, fontSize: 13.5, fontWeight: 700 }}>Aree di competenza</span>
        </div>
        <p className="px-1 mb-3" style={{ color: C.mutedDim, fontSize: 11.5, lineHeight: 1.5, maxWidth: 720 }}>
          La preparazione di base di VIKI, organizzata come un vero piano di studi: tocca un'area per aprirla.
        </p>
        <KnowledgeAreaAccordion />
      </div>

      <div className="flex items-center gap-2 px-1 mt-2">
        <Boxes size={15} style={{ color: C.cyan }} />
        <span style={{ color: C.text, fontSize: 13.5, fontWeight: 700 }}>Domini operativi PAQ</span>
      </div>
      <p className="px-1" style={{ color: C.mutedDim, fontSize: 11.5, lineHeight: 1.5, maxWidth: 720, marginTop: -8 }}>
        Gli stessi saperi, applicati concretamente alle Funzioni e ai moduli dell'ecosistema.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {KNOWLEDGE_DOMAINS.map((k, i) => (
          <Card key={k.key} index={i} style={{ padding: 16 }}>
            <div className="flex items-center gap-2.5 mb-2">
              <div className="flex items-center justify-center shrink-0" style={{ width: 32, height: 32, borderRadius: 8, background: C.cyanDim, color: C.cyan }}>
                <k.icon size={15} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{k.label}</span>
                  <span style={{ color: C.cyan, fontSize: 12, ...mono }}>{Math.round(k.value * 100)}%</span>
                </div>
              </div>
            </div>
            <div style={{ height: 6, background: C.surfaceAlt, borderRadius: 3, overflow: "hidden", marginBottom: 8 }}>
              <div style={{ width: `${k.value * 100}%`, height: "100%", background: C.cyan }} />
            </div>
            <div style={{ color: C.mutedDim, fontSize: 11.5, lineHeight: 1.5 }}>{k.desc}</div>
          </Card>
        ))}
      </div>
      <div className="flex items-start gap-2 px-1" style={{ color: C.mutedDim, fontSize: 11, maxWidth: 720 }}>
        <AlertTriangle size={13} style={{ marginTop: 1, flexShrink: 0, color: C.amber }} />
        <span>Su diritto privato e penale, VIKI segnala i rischi e organizza le informazioni: non sostituisce mai un parere legale professionale.</span>
      </div>

      <Card style={{ padding: 16 }}>
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp size={14} style={{ color: C.cyan }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Marketing digitale — dettaglio</span>
        </div>
        <p style={{ color: C.mutedDim, fontSize: 11, marginBottom: 12, lineHeight: 1.5 }}>
          Il dominio Marketing si scompone in sei aree operative, ciascuna con la propria maturità.
        </p>
        <div className="flex flex-col gap-3">
          {MARKETING_SKILLS.map((m) => (
            <div key={m.key}>
              <div className="flex items-center justify-between mb-1">
                <span style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{m.label}</span>
                <span style={{ color: C.cyan, fontSize: 11, ...mono }}>{Math.round(m.value * 100)}%</span>
              </div>
              <div style={{ height: 5, background: C.surfaceAlt, borderRadius: 3, overflow: "hidden", marginBottom: 3 }}>
                <div style={{ width: `${m.value * 100}%`, height: "100%", background: C.cyan }} />
              </div>
              <div style={{ color: C.mutedDim, fontSize: 10.5, lineHeight: 1.4 }}>{m.desc}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card style={{ padding: 16 }}>
        <div className="flex items-center gap-2 mb-1">
          <Code2 size={14} style={{ color: C.cyan }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Revisione del codice</span>
        </div>
        <p style={{ color: C.mutedDim, fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>
          Non un consiglio a parole: un vero confronto riga per riga, come farebbe un collega che rilegge il tuo codice.
        </p>
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
          <div className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: `1px solid ${C.border}`, background: C.surfaceAlt }}>
            <span style={{ color: C.mutedDim, fontSize: 10.5, ...mono }}>Posta AI · gestione allegati</span>
            <span style={{ color: C.rose, fontSize: 9.5, background: C.roseDim, padding: "1px 7px", borderRadius: 4, fontWeight: 600 }}>rischio</span>
          </div>
          <div className="p-3" style={{ ...mono, fontSize: 11, lineHeight: 1.7, overflowX: "auto" }}>
            <div style={{ background: C.roseDim, color: C.rose, padding: "1px 8px", whiteSpace: "pre" }}>- const size = file.size / 1024 / 1024;</div>
            <div style={{ background: C.roseDim, color: C.rose, padding: "1px 8px", whiteSpace: "pre" }}>- // nessun controllo: un allegato enorme blocca il server</div>
            <div style={{ background: C.cyanDim, color: C.cyan, padding: "1px 8px", whiteSpace: "pre" }}>+ const size = file.size / 1024 / 1024;</div>
            <div style={{ background: C.cyanDim, color: C.cyan, padding: "1px 8px", whiteSpace: "pre" }}>{"+ if (size > 25) throw new Error(\"Allegato troppo grande (max 25MB)\");"}</div>
          </div>
        </div>
        <div className="flex items-start gap-1.5 mt-2.5">
          <Brain size={11} style={{ color: C.violet, marginTop: 2, flexShrink: 0 }} />
          <span style={{ color: C.muted, fontSize: 11.5, lineHeight: 1.5 }}>Senza un limite esplicito, un allegato molto grande può saturare la memoria del server prima ancora di essere salvato: la correzione aggiunge il controllo mancante, senza cambiare il resto della logica.</span>
        </div>
      </Card>

      <Card style={{ padding: 16 }}>
        <div className="flex items-center gap-2 mb-1">
          <Palette size={14} style={{ color: C.cyan }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Guida di stile PAQ</span>
        </div>
        <p style={{ color: C.mutedDim, fontSize: 11, marginBottom: 12, lineHeight: 1.5 }}>
          Gli stessi token visivi di VIKI diventano lo standard condiviso: ogni modulo PAQ dovrebbe riconoscersi in questa palette.
        </p>
        <div className="flex gap-2 mb-3 flex-wrap">
          {[
            { name: "Sfondo", color: C.bg }, { name: "Superficie", color: C.surface },
            { name: "Ciano", color: C.cyan }, { name: "Ambra", color: C.amber },
            { name: "Rosa", color: C.rose }, { name: "Violetto", color: C.violet },
          ].map((sw) => (
            <div key={sw.name} className="flex flex-col items-center gap-1">
              <div style={{ width: 34, height: 34, borderRadius: 8, background: sw.color, border: `1px solid ${C.border}` }} />
              <span style={{ color: C.mutedDim, fontSize: 9 }}>{sw.name}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-1.5 p-2.5 rounded-md" style={{ background: C.surfaceAlt }}>
          <span style={{ color: C.text, fontSize: 15, fontWeight: 700 }}>Titolo — Inter, grassetto</span>
          <span style={{ color: C.muted, fontSize: 12 }}>Testo corrente — Inter, regolare, per leggibilità nei contenuti</span>
          <span style={{ color: C.mutedDim, fontSize: 11, ...mono }}>Dati e cifre — IBM Plex Mono, per numeri e stati</span>
        </div>
        <div className="flex items-start gap-1.5 mt-2.5">
          <Brain size={11} style={{ color: C.violet, marginTop: 2, flexShrink: 0 }} />
          <span style={{ color: C.muted, fontSize: 11.5, lineHeight: 1.5 }}>Quando un modulo PAQ si discosta da questi riferimenti, VIKI lo segnala come disallineamento visivo, non come errore bloccante.</span>
        </div>
      </Card>
    </div>
  );
}

/* ================= Integrazioni PAQ ================= */
/* ---------- mappa a sinapsi: stesso linguaggio visivo del cervello,
   applicato alla scala dell'ecosistema — VIKI al centro, ogni modulo PAQ
   un nodo periferico. Le sinapsi connesse pulsano, quelle da collegare
   restano spente, per rendere visibile cosa alimenta davvero VIKI oggi. */
function SynapseMap({ items }) {
  const list = items || PAQ_INTEGRATIONS;
  const cx = 210, cy = 210, radius = 148;
  const nodes = list.map((it, i) => {
    const angle = (i / list.length) * 2 * Math.PI - Math.PI / 2;
    return { ...it, x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
  });

  return (
    <Card style={{ padding: 20 }}>
      <div className="flex items-center gap-2 mb-1">
        <Zap size={15} style={{ color: C.cyan }} />
        <span style={{ color: C.text, fontSize: 13.5, fontWeight: 600 }}>Sinapsi dell'ecosistema</span>
      </div>
      <p style={{ color: C.mutedDim, fontSize: 11.5, marginBottom: 8, lineHeight: 1.5 }}>
        I moduli già connessi alimentano davvero VIKI; gli altri restano spenti finché non li colleghi.
      </p>
      <div style={{ width: "100%", maxWidth: 420, margin: "0 auto", position: "relative" }}>
        <style>{`
          @keyframes synapsePulse { 0%,100% { stroke-opacity: 0.85; } 50% { stroke-opacity: 0.3; } }
          @keyframes synapseSignal { 0% { stroke-dashoffset: 30; opacity: 0; } 15% { opacity: 1; } 85% { opacity: 1; } 100% { stroke-dashoffset: 0; opacity: 0; } }
          @keyframes synapseCore { 0%,100% { r: 26; } 50% { r: 29; } }
          .synapse-live { animation: synapsePulse 3.2s ease-in-out infinite; }
          .synapse-signal { stroke-dasharray: 8 22; animation: synapseSignal 2.6s linear infinite; }
          .synapse-core { animation: synapseCore 3.6s ease-in-out infinite; }
        `}</style>
        <svg viewBox="0 0 420 420" width="100%" height="100%">
          {nodes.map((n) => {
            const connected = n.status === "Connesso";
            return (
              <line key={`e-${n.id}`} x1={cx} y1={cy} x2={n.x} y2={n.y}
                stroke={connected ? C.cyan : C.borderSoft} strokeWidth={connected ? 1.6 : 1}
                strokeDasharray={connected ? undefined : "3 5"}
                className={connected ? "synapse-live" : ""} />
            );
          })}
          {nodes.filter((n) => n.status === "Connesso").map((n) => (
            <line key={`s-${n.id}`} x1={cx} y1={cy} x2={n.x} y2={n.y} stroke={C.text} strokeWidth={1.4} strokeLinecap="round" className="synapse-signal" />
          ))}
          <circle cx={cx} cy={cy} r={26} fill={C.cyanDim} stroke={C.cyan} strokeWidth={1.4} className="synapse-core" />
          <text x={cx} y={cy + 4} textAnchor="middle" fontSize="11" fontWeight="700" fill={C.cyan} style={{ fontFamily: mono.fontFamily }}>VIKI</text>
          {nodes.map((n) => {
            const connected = n.status === "Connesso";
            return (
              <g key={n.id}>
                <circle cx={n.x} cy={n.y} r={17} fill={connected ? C.surfaceAlt : C.surface} stroke={connected ? C.cyan : C.border} strokeWidth={1.2} />
                <text x={n.x} y={n.y + 4} textAnchor="middle" fontSize="10" fill={connected ? C.cyan : C.mutedDim}>●</text>
              </g>
            );
          })}
        </svg>
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {nodes.map((n) => {
            const px = (n.x / 420) * 100, py = (n.y / 420) * 100;
            const connected = n.status === "Connesso";
            return (
              <div key={`l-${n.id}`} style={{
                position: "absolute", left: `${px}%`, top: `${py}%`, transform: "translate(-50%, 130%)",
                fontSize: 9.5, color: connected ? C.text : C.mutedDim, whiteSpace: "nowrap", fontWeight: connected ? 600 : 500,
              }}>{n.name}</div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

function IntegrationsTab() {
  const items = PAQ_INTEGRATIONS;
  const loading = false;
  return (
    <div className="p-6 flex flex-col gap-4">
      <SectionIntro icon={Info} title="Integrazioni PAQ">
        ogni modulo dell'ecosistema che VIKI può leggere, capire e su cui può agire. Si collegano uno alla volta, senza fretta.
      </SectionIntro>
      <EcosystemDbStatus />
      {!loading && <SynapseMap items={items} />}
      {loading ? <LoadingRow /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(items || []).map((it, i) => {
            const st = INTEGRATION_STYLE[it.status];
            return (
              <Card key={it.id} index={i} style={{ padding: 16 }}>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center shrink-0" style={{ width: 34, height: 34, borderRadius: 9, background: C.surfaceAlt, color: C.muted }}>
                    <it.icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{it.name}</span>
                      <span style={{ color: st.color, fontSize: 10.5, background: st.bg, padding: "2px 8px", borderRadius: 20, fontWeight: 600, whiteSpace: "nowrap" }}>{it.status}</span>
                    </div>
                    <span style={{ color: C.mutedDim, fontSize: 10.5, ...mono }}>{it.category}</span>
                    <div style={{ color: C.muted, fontSize: 11.5, marginTop: 4, lineHeight: 1.4 }}>{it.detail}</div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ================= Governance ================= */
function formatDecisionTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `oggi · ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `ieri · ${time}`;
  return `${d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })} · ${time}`;
}

function GovernanceTab() {
  const [decisions, setDecisions] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/viki/decision-log")
      .then(({ data }) => { if (!cancelled) setDecisions(data.decisions || []); })
      .catch(() => { if (!cancelled) setDecisions([]); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="p-6 flex flex-col gap-4">
      <SectionIntro icon={Info} title="Governance">
        dove VIKI può agire da sola, dove deve chiedere conferma, e cosa ha già deciso. Il controllo resta sempre umano nelle aree critiche.
      </SectionIntro>

      {CONFLICTS.length > 0 && (
        <Card style={{ padding: 16, borderColor: `${C.rose}44` }}>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={14} style={{ color: C.rose }} />
            <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Conflitti rilevati</span>
          </div>
          <p style={{ color: C.mutedDim, fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>
            Quando una Funzione e un Suggerimento si contraddicono, VIKI si ferma e chiede a te, invece di scegliere da sola quale applicare.
          </p>
          <div className="flex flex-col gap-2.5">
            {CONFLICTS.map((c) => {
              const st = CONFLICT_STATUS_STYLE[c.status];
              return (
                <div key={c.id} className="p-3 rounded-md" style={{ background: C.surfaceAlt }}>
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                    <span style={{ color: C.rose, fontSize: 10.5, background: C.roseDim, padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>{c.a.type}</span>
                    <X size={11} style={{ color: C.mutedDim }} />
                    <span style={{ color: C.amber, fontSize: 10.5, background: C.amberDim, padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>{c.b.type}</span>
                    <span className="ml-auto shrink-0" style={{ color: st.color, fontSize: 10.5, background: st.bg, padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>{c.status}</span>
                  </div>
                  <div style={{ color: C.text, fontSize: 12, lineHeight: 1.5, marginBottom: 3 }}>"{c.a.label}"</div>
                  <div style={{ color: C.text, fontSize: 12, lineHeight: 1.5, marginBottom: 8 }}>"{c.b.label}"</div>
                  <div className="flex items-start gap-1.5">
                    <Brain size={11} style={{ color: C.violet, marginTop: 2, flexShrink: 0 }} />
                    <span style={{ color: C.muted, fontSize: 11.5, lineHeight: 1.5 }}>{c.explanation}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card style={{ padding: 16, borderColor: `${C.amber}44` }}>
        <div className="flex items-center gap-2 mb-1">
          <Activity size={14} style={{ color: C.amber }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Anomalie finanziarie</span>
        </div>
        <p style={{ color: C.mutedDim, fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>
          Pattern insoliti nei movimenti contabili, segnalati prima che diventino un problema — VIKI non li corregge da sola.
        </p>
        <div className="flex flex-col gap-2">
          {FINANCIAL_ANOMALIES.map((a) => {
            const st = ANOMALY_SEVERITY_STYLE[a.severity];
            return (
              <div key={a.id} className="flex items-center gap-3 p-2.5 rounded-md" style={{ background: C.surfaceAlt }}>
                <div className="flex-1 min-w-0">
                  <span style={{ color: C.mutedDim, fontSize: 10, ...mono, background: C.surface, padding: "1px 7px", borderRadius: 4, border: `1px solid ${C.border}`, marginRight: 6 }}>{a.area}</span>
                  <span style={{ color: C.text, fontSize: 12, lineHeight: 1.5 }}>{a.description}</span>
                </div>
                <span className="shrink-0" style={{ color: st.color, fontSize: 10.5, background: st.bg, padding: "3px 9px", borderRadius: 20, fontWeight: 600 }}>{a.severity}</span>
              </div>
            );
          })}
        </div>
      </Card>

      <Card style={{ padding: 16 }}>
        <div className="flex items-center gap-2 mb-1">
          <Eye size={14} style={{ color: C.violet }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Monitoraggio &amp; suggerimenti</span>
        </div>
        <p style={{ color: C.mutedDim, fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>
          VIKI osserva come lavorano gli utenti nell'ecosistema e propone miglioramenti — senza mai agire da sola su questo.
        </p>
        <div className="flex flex-col gap-2">
          {MONITORED_ACTIVITY.map((m) => (
            <div key={m.id} className="p-2.5 rounded-md" style={{ background: C.surfaceAlt }}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span style={{ color: C.mutedDim, fontSize: 10, ...mono, background: C.surface, padding: "1px 7px", borderRadius: 4, border: `1px solid ${C.border}` }}>{m.area}</span>
                <span style={{ color: C.mutedDim, fontSize: 10.5 }}>{m.user}</span>
              </div>
              <div style={{ color: C.text, fontSize: 12, lineHeight: 1.5, marginBottom: 4 }}>{m.action}</div>
              <div className="flex items-start gap-1.5">
                <Lightbulb size={12} style={{ color: C.amber, marginTop: 2, flexShrink: 0 }} />
                <span style={{ color: C.muted, fontSize: 11.5, lineHeight: 1.5 }}>{m.suggestion}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card style={{ padding: 16 }}>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={14} style={{ color: C.cyan }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Sicurezza &amp; manutenzione</span>
        </div>
        <p style={{ color: C.mutedDim, fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>
          Controlli continui sullo stato di salute dei moduli PAQ, non solo su cosa fanno gli utenti.
        </p>
        <div className="flex flex-col gap-2">
          {SECURITY_CHECKS.map((s) => {
            const st = SECURITY_STATUS_STYLE[s.status];
            return (
              <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-md" style={{ background: C.surfaceAlt }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{s.module}</span>
                    <span style={{ color: C.mutedDim, fontSize: 11 }}>· {s.check}</span>
                  </div>
                  <div style={{ color: C.mutedDim, fontSize: 10.5, marginTop: 2 }}>ultimo controllo: {s.lastCheck}</div>
                </div>
                <span className="flex items-center gap-1 shrink-0" style={{ color: st.color, fontSize: 10.5, background: st.bg, padding: "3px 9px", borderRadius: 20, fontWeight: 600 }}>
                  <st.icon size={11} /> {s.status}
                </span>
              </div>
            );
          })}
        </div>
      </Card>

      <Card style={{ padding: 16 }}>
        <div className="flex items-center gap-2 mb-1">
          <UserCheck size={14} style={{ color: C.cyan }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Permessi ereditati (RBAC)</span>
        </div>
        <p style={{ color: C.mutedDim, fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>
          VIKI non ha permessi propri: vede esattamente quello che vede {RECOGNIZED_IDENTITY.user}, né un dato in più.
        </p>
        <div className="flex flex-col gap-2">
          {RBAC_PERMISSIONS.map((p, i) => (
            <div key={i} className="flex items-center gap-3 p-2.5 rounded-md" style={{ background: C.surfaceAlt }}>
              {p.allowed ? <CheckCircle2 size={14} style={{ color: C.cyan, flexShrink: 0 }} /> : <Lock size={14} style={{ color: C.rose, flexShrink: 0 }} />}
              <span style={{ color: C.text, fontSize: 12, flex: 1 }}>{p.area}</span>
              <span style={{ color: p.allowed ? C.cyan : C.rose, fontSize: 10.5, background: p.allowed ? C.cyanDim : C.roseDim, padding: "2px 9px", borderRadius: 20, fontWeight: 600 }}>
                {p.allowed ? "Consentito" : "Non consentito"}
              </span>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex flex-col gap-2.5">
        {AUTONOMY_LEVELS.map((a, i) => {
          const st = AUTONOMY_STYLE[a.level];
          return (
            <Card key={a.area} index={i} style={{ padding: 14 }}>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center shrink-0" style={{ width: 32, height: 32, borderRadius: 8, background: C.surfaceAlt, color: C.muted }}>
                  <a.icon size={15} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{a.area}</span>
                    <span className="flex items-center gap-1" style={{ color: st.color, fontSize: 10.5, background: st.bg, padding: "2px 9px", borderRadius: 20, fontWeight: 600 }}>
                      <st.icon size={11} /> {a.level}
                    </span>
                  </div>
                  <div style={{ color: C.muted, fontSize: 11.5, marginTop: 3, lineHeight: 1.4 }}>{a.desc}</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Card style={{ padding: 16 }}>
        <div className="flex items-center gap-2 mb-3">
          <Clock size={14} style={{ color: C.cyan }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Ultime decisioni</span>
        </div>
        {decisions === null ? (
          <LoadingRow />
        ) : decisions.length === 0 ? (
          <div style={{ color: C.mutedDim, fontSize: 11.5, lineHeight: 1.5, padding: "8px 2px" }}>
            Ancora nessuna decisione registrata: qui comparirà ogni azione reale che VIKI propone o esegue, appena inizierai a usarla.
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {decisions.map((d) => {
              const st = DECISION_STATUS_STYLE[d.status] || DECISION_STATUS_STYLE["In attesa"];
              return (
                <div key={d.id} className="flex items-start gap-3 p-2.5 rounded-md" style={{ background: C.surfaceAlt }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span style={{ color: C.mutedDim, fontSize: 10, ...mono, background: C.surface, padding: "1px 7px", borderRadius: 4, border: `1px solid ${C.border}` }}>{d.area}</span>
                      <span style={{ color: C.mutedDim, fontSize: 10.5 }}>{formatDecisionTime(d.time)}</span>
                    </div>
                    <div style={{ color: C.text, fontSize: 12, lineHeight: 1.5 }}>{d.action}</div>
                  </div>
                  <span className="flex items-center gap-1 shrink-0" style={{ color: st.color, fontSize: 10.5, background: st.bg, padding: "3px 9px", borderRadius: 20, fontWeight: 600 }}>
                    <st.icon size={11} /> {d.status}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card style={{ padding: 16 }}>
        <div className="flex items-center gap-2 mb-1">
          <Package size={14} style={{ color: C.amber }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Ponte con Risorice</span>
        </div>
        <p style={{ color: C.mutedDim, fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>
          Risorice è già pronta per gli store: le decisioni in attesa possono raggiungerti lì, non solo al login sul browser.
        </p>
        <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, maxWidth: 360 }}>
          <div className="flex items-center justify-center shrink-0" style={{ width: 34, height: 34, borderRadius: 9, background: C.cyanDim, color: C.cyan }}>
            <Brain size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span style={{ color: C.text, fontSize: 11.5, fontWeight: 700 }}>VIKI · AreaPaq</span>
              <span style={{ color: C.mutedDim, fontSize: 9.5 }}>ora</span>
            </div>
            <div style={{ color: C.muted, fontSize: 11.5, lineHeight: 1.4, marginTop: 1 }}>
              {DECISION_LOG.filter((d) => d.status === "In attesa").length} decisioni aspettano la tua conferma in Governance
            </div>
          </div>
        </div>
        <div className="flex items-start gap-1.5 mt-3">
          <Info size={11} style={{ color: C.mutedDim, marginTop: 2, flexShrink: 0 }} />
          <span style={{ color: C.mutedDim, fontSize: 10.5, lineHeight: 1.5 }}>Anteprima concettuale: l'invio reale richiede il collegamento tra VIKI e l'app Risorice pubblicata sugli store.</span>
        </div>
      </Card>

      <Card style={{ padding: 16 }}>
        <div className="flex items-center gap-2 mb-2">
          <Lock size={14} style={{ color: C.cyan }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Dati puliti e protetti</span>
        </div>
        <p style={{ color: C.muted, fontSize: 12, lineHeight: 1.6 }}>
          Ogni fonte collegata in Integrazioni PAQ deve restare tracciabile e aggiornata: VIKI non prende decisioni
          su dati di cui non conosce la provenienza. I dati sensibili dell'ecosistema PAQ non escono mai verso
          servizi esterni non autorizzati.
        </p>
      </Card>
    </div>
  );
}

/* ================= Scadenzario ================= */
function DeadlinesTab() {
  const sorted = [...DEADLINES].sort((a, b) => a.daysLeft - b.daysLeft);
  const urgentCount = DEADLINES.filter((d) => d.urgency === "Urgente").length;
  return (
    <div className="p-6 flex flex-col gap-4">
      <SectionIntro icon={Info} title="Scadenzario">
        scadenze fiscali, normative e contrattuali che VIKI tiene d'occhio per te, così non arrivano di sorpresa.
      </SectionIntro>

      {urgentCount > 0 && (
        <div className="flex items-start gap-2 px-1" style={{ color: C.rose, fontSize: 11.5 }}>
          <AlertTriangle size={13} style={{ marginTop: 1, flexShrink: 0 }} />
          <span>{urgentCount} scadenz{urgentCount === 1 ? "a" : "e"} entro 25 giorni: dai priorità a quelle in cima alla lista.</span>
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        {sorted.map((d, i) => {
          const Icon = DEADLINE_CATEGORIES[d.category] || FileText;
          const st = DEADLINE_URGENCY_STYLE[d.urgency];
          return (
            <Card key={d.id} index={i} style={{ padding: 14 }}>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center shrink-0" style={{ width: 32, height: 32, borderRadius: 8, background: C.surfaceAlt, color: C.muted }}>
                  <Icon size={15} />
                </div>
                <div className="flex-1 min-w-0">
                  <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{d.item}</div>
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    <span style={{ color: C.mutedDim, fontSize: 10.5, ...mono, background: C.surfaceAlt, padding: "2px 7px", borderRadius: 4 }}>{d.category}</span>
                    <span style={{ color: C.mutedDim, fontSize: 10.5 }}>scade il {d.dueDate}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span style={{ color: st.color, fontSize: 10.5, background: st.bg, padding: "2px 9px", borderRadius: 20, fontWeight: 600 }}>{d.urgency}</span>
                  <span style={{ color: C.mutedDim, fontSize: 10, ...mono }}>tra {d.daysLeft}gg</span>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="flex items-start gap-2 px-1" style={{ color: C.mutedDim, fontSize: 11, maxWidth: 720 }}>
        <Info size={13} style={{ marginTop: 1, flexShrink: 0, color: C.cyan }} />
        <span>Collegato alle Funzioni di Contabilità: quando una scadenza fiscale si avvicina, VIKI può già preparare la documentazione, restando comunque in attesa della tua conferma prima di procedere.</span>
      </div>
    </div>
  );
}

/* ================= Onboarding ================= */
function OnboardingTab() {
  const doneCount = ONBOARDING_STEPS.filter((s) => s.done).length;
  return (
    <div className="p-6 flex flex-col gap-4">
      <SectionIntro icon={Info} title="Onboarding">
        VIKI accompagna ogni nuovo collaboratore nei primi giorni, spiegando i processi interni invece di lasciare che li chieda in giro.
      </SectionIntro>

      <Card style={{ padding: 16 }}>
        <div className="flex items-center gap-2 mb-1">
          <GraduationCap size={14} style={{ color: C.cyan }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Percorso del nuovo collaboratore</span>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <div style={{ flex: 1, height: 6, background: C.surfaceAlt, borderRadius: 3, overflow: "hidden" }}>
            <div style={{ width: `${(doneCount / ONBOARDING_STEPS.length) * 100}%`, height: "100%", background: C.cyan }} />
          </div>
          <span style={{ color: C.cyan, fontSize: 11, ...mono }}>{doneCount}/{ONBOARDING_STEPS.length}</span>
        </div>
        <div className="flex flex-col gap-2.5">
          {ONBOARDING_STEPS.map((s) => (
            <div key={s.id} className="flex items-start gap-3">
              {s.done ? <CheckCircle2 size={16} style={{ color: C.cyan, marginTop: 1, flexShrink: 0 }} /> : <Circle size={16} style={{ color: C.mutedDim, marginTop: 1, flexShrink: 0 }} />}
              <div>
                <div style={{ color: s.done ? C.text : C.muted, fontSize: 12.5, fontWeight: 600 }}>{s.title}</div>
                <div style={{ color: C.mutedDim, fontSize: 11.5, marginTop: 2, lineHeight: 1.5 }}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex items-start gap-2 px-1" style={{ color: C.mutedDim, fontSize: 11, maxWidth: 720 }}>
        <Info size={13} style={{ marginTop: 1, flexShrink: 0, color: C.cyan }} />
        <span>Percorso pensato per essere seguito nei primi giorni: VIKI segnala cosa manca ancora, senza sostituirsi a un collega vero per le domande più specifiche.</span>
      </div>
    </div>
  );
}

/* ================= Staff Virtuale ================= */
function StaffTab() {
  return (
    <div className="p-6 flex flex-col gap-4">
      <SectionIntro icon={Info} title="Staff Virtuale">
        quattro profili specializzati della stessa VIKI, non quattro intelligenze separate. Ognuno guarda l'ecosistema dal proprio ambito.
      </SectionIntro>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {VIRTUAL_STAFF.map((s, i) => {
          const st = STAFF_STATUS_STYLE[s.status];
          return (
            <Card key={s.id} index={i} style={{ padding: 16 }}>
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center shrink-0" style={{ width: 36, height: 36, borderRadius: 10, background: C.cyanDim, color: C.cyan }}>
                  <s.icon size={17} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{s.role}</span>
                    <span style={{ color: st.color, fontSize: 10, background: st.bg, padding: "2px 8px", borderRadius: 20, fontWeight: 600, whiteSpace: "nowrap" }}>{s.status}</span>
                  </div>
                  <div style={{ color: C.muted, fontSize: 11.5, marginTop: 4, lineHeight: 1.4 }}>{s.focus}</div>
                  <div style={{ color: C.mutedDim, fontSize: 10, ...mono, marginTop: 6 }}>{s.linkedTo}</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      <div className="flex items-start gap-2 px-1" style={{ color: C.mutedDim, fontSize: 11, maxWidth: 720 }}>
        <Info size={13} style={{ marginTop: 1, flexShrink: 0, color: C.cyan }} />
        <span>Ogni "responsabile" eredita la stessa Conoscenza e le stesse regole del resto di VIKI: cambia solo il filtro con cui guarda i dati, non il cervello dietro.</span>
      </div>
    </div>
  );
}

/* ================= Centralino ================= */
function CentralinoTab() {
  return (
    <div className="p-6 flex flex-col gap-4">
      <SectionIntro icon={Info} title="Centralino">
        rispondere al telefono, fissare appuntamenti e gestire le richieste in arrivo con la stessa voce e lo stesso stile di VIKI.
      </SectionIntro>

      <Card style={{ padding: 16, borderColor: `${C.amber}44` }}>
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle size={14} style={{ color: C.amber }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>In fase di progettazione</span>
        </div>
        <p style={{ color: C.muted, fontSize: 12, lineHeight: 1.6 }}>
          Il centralino richiede un modello vocale reale da integrare (voce registrata, sintesi vocale, linea telefonica collegata).
          Quello che vedi qui sotto è un'anteprima concettuale di come apparirà una volta attivo, non una funzione già operativa.
        </p>
      </Card>

      <Card style={{ padding: 16 }}>
        <div className="flex items-center gap-2 mb-3">
          <Package size={14} style={{ color: C.violet }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Modello vocale</span>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span style={{ color: C.muted, fontSize: 12 }}>Voce da registrazione, da integrare nel motore di risposta</span>
          <span style={{ color: C.mutedDim, fontSize: 10.5, background: C.surfaceAlt, padding: "3px 9px", borderRadius: 20, fontWeight: 600, border: `1px solid ${C.border}` }}>Da integrare</span>
        </div>
      </Card>

      <Card style={{ padding: 16 }}>
        <div className="flex items-center gap-2 mb-3">
          <Clock size={14} style={{ color: C.cyan }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Anteprima: chiamate ricevute</span>
        </div>
        <div className="flex flex-col gap-2">
          {CALL_LOG.map((c) => (
            <div key={c.id} className="p-2.5 rounded-md" style={{ background: C.surfaceAlt }}>
              <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                <span style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{c.caller}</span>
                <span style={{ color: C.mutedDim, fontSize: 10.5 }}>{c.time} · {c.duration}</span>
              </div>
              <div style={{ color: C.muted, fontSize: 11.5, lineHeight: 1.4 }}>{c.outcome}</div>
            </div>
          ))}
        </div>
      </Card>

    </div>
  );
}

/* ================= Riunioni ================= */
function MeetingsTab() {
  return (
    <div className="p-6 flex flex-col gap-4">
      <SectionIntro icon={Info} title="Riunioni">
        carica verbale o registrazione, VIKI estrae decisioni e prossimi passi e li smista dove servono.
      </SectionIntro>
      <div className="flex flex-col gap-3">
        {MEETINGS.map((m, i) => (
          <Card key={m.id} index={i} style={{ padding: 16 }}>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
              <span style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{m.title}</span>
              <span style={{ color: C.mutedDim, fontSize: 10.5, ...mono }}>{m.date}</span>
            </div>
            <div className="mb-3">
              <div style={{ color: C.mutedDim, fontSize: 10.5, marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.4 }}>Decisioni prese</div>
              <div className="flex flex-col gap-1.5">
                {m.decisions.map((d, j) => (
                  <div key={j} className="flex items-start gap-1.5">
                    <CheckCircle2 size={12} style={{ color: C.cyan, marginTop: 2, flexShrink: 0 }} />
                    <span style={{ color: C.text, fontSize: 12, lineHeight: 1.5 }}>{d}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mb-3">
              <div style={{ color: C.mutedDim, fontSize: 10.5, marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.4 }}>Prossimi passi</div>
              <div className="flex flex-col gap-1.5">
                {m.nextSteps.map((s, j) => (
                  <div key={j} className="flex items-start gap-1.5">
                    <ChevronRight size={12} style={{ color: C.amber, marginTop: 2, flexShrink: 0 }} />
                    <span style={{ color: C.muted, fontSize: 12, lineHeight: 1.5 }}>{s}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Link2 size={11} style={{ color: C.mutedDim }} />
              {m.routedTo.map((r) => (
                <span key={r} style={{ color: C.mutedDim, fontSize: 10, ...mono, background: C.surfaceAlt, padding: "1px 7px", borderRadius: 4, border: `1px solid ${C.border}` }}>{r}</span>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ================= CRM leggero ================= */
function CrmTab() {
  return (
    <div className="p-6 flex flex-col gap-4">
      <SectionIntro icon={Info} title="CRM">
        storico essenziale per cliente: ultimo contatto, stato della relazione, prossima azione consigliata.
      </SectionIntro>
      <div className="flex flex-col gap-2.5">
        {CRM_CLIENTS.map((c, i) => {
          const st = CRM_STATUS_STYLE[c.status];
          return (
            <Card key={c.id} index={i} style={{ padding: 14 }}>
              <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
                <span style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{c.name}</span>
                <span style={{ color: st.color, fontSize: 10.5, background: st.bg, padding: "2px 9px", borderRadius: 20, fontWeight: 600 }}>{c.status}</span>
              </div>
              <div style={{ color: C.mutedDim, fontSize: 10.5, marginBottom: 6 }}>ultimo contatto: {c.lastContact}</div>
              <div className="flex items-start gap-1.5">
                <Lightbulb size={12} style={{ color: C.amber, marginTop: 2, flexShrink: 0 }} />
                <span style={{ color: C.muted, fontSize: 11.5, lineHeight: 1.5 }}>{c.nextAction}</span>
              </div>
            </Card>
          );
        })}
      </div>
      <div className="flex items-start gap-2 px-1" style={{ color: C.mutedDim, fontSize: 11, maxWidth: 720 }}>
        <Info size={13} style={{ marginTop: 1, flexShrink: 0, color: C.cyan }} />
        <span>Versione essenziale: storico strutturato per cliente, non un CRM completo. Cresce con le interazioni reali via Posta AI e Centralino.</span>
      </div>
    </div>
  );
}

/* ================= Modalità Simulazione ================= */
function SimulationTab() {
  const [selected, setSelected] = useState(null);
  const scenario = SIMULATION_SCENARIOS.find((s) => s.id === selected);
  return (
    <div className="p-6 flex flex-col gap-4">
      <SectionIntro icon={Info} title="Simulazione">
        scenari interi da esplorare prima di viverli davvero, come la Sandbox delle Funzioni ma su decisioni strategiche.
      </SectionIntro>
      <div className="flex flex-col gap-2">
        {SIMULATION_SCENARIOS.map((s) => (
          <button key={s.id} onClick={() => setSelected(s.id === selected ? null : s.id)} className="mind-btn text-left p-3 rounded-md"
            style={{ background: selected === s.id ? C.cyanDim : C.surfaceAlt, border: `1px solid ${selected === s.id ? C.cyan + "66" : C.border}`, color: C.text, fontSize: 12.5, fontWeight: 600 }}>
            {s.question}
          </button>
        ))}
      </div>
      {scenario && (
        <Card style={{ padding: 16 }}>
          <div className="flex items-start gap-1.5 mb-3">
            <Activity size={13} style={{ color: C.amber, marginTop: 2, flexShrink: 0 }} />
            <div>
              <div style={{ color: C.mutedDim, fontSize: 10.5, marginBottom: 3 }}>Impatto stimato</div>
              <div style={{ color: C.text, fontSize: 12.5, lineHeight: 1.6 }}>{scenario.impact}</div>
            </div>
          </div>
          <div className="flex items-start gap-1.5">
            <Lightbulb size={13} style={{ color: C.cyan, marginTop: 2, flexShrink: 0 }} />
            <div>
              <div style={{ color: C.mutedDim, fontSize: 10.5, marginBottom: 3 }}>Cosa consiglia VIKI</div>
              <div style={{ color: C.text, fontSize: 12.5, lineHeight: 1.6 }}>{scenario.recommendation}</div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ================= Memoria ================= */
function MemoryTab() {
  return (
    <div className="p-6 flex flex-col gap-4">
      <SectionIntro icon={Info} title="Memoria">
        quello che VIKI ricorda diventa la base per decidere meglio la volta successiva. Qui sotto lo stato dei suoi blocchi di memoria persistente.
      </SectionIntro>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {MEMORY_BLOCKS.map((m, i) => (
          <Card key={m.key} index={i} style={{ padding: 16 }}>
            <div className="flex items-center gap-2.5 mb-2">
              <div className="flex items-center justify-center shrink-0" style={{ width: 32, height: 32, borderRadius: 8, background: C.violetDim, color: C.violet }}>
                <m.icon size={15} />
              </div>
              <div className="flex-1 flex items-center justify-between">
                <span style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{m.label}</span>
                <span style={{ color: C.violet, fontSize: 16, fontWeight: 700, ...mono }}>{m.count}</span>
              </div>
            </div>
            <div style={{ color: C.mutedDim, fontSize: 11.5, lineHeight: 1.5 }}>{m.desc}</div>
          </Card>
        ))}
      </div>

      <Card style={{ padding: 16 }}>
        <div className="flex items-center gap-2 mb-2">
          <UserCheck size={14} style={{ color: C.cyan }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Identità riconosciuta</span>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{RECOGNIZED_IDENTITY.user}</div>
            <div style={{ color: C.mutedDim, fontSize: 11, marginTop: 2 }}>{RECOGNIZED_IDENTITY.modules} · via {RECOGNIZED_IDENTITY.method}</div>
          </div>
          <span style={{ color: C.cyan, fontSize: 10.5, background: C.cyanDim, padding: "3px 9px", borderRadius: 20, fontWeight: 600 }}>{RECOGNIZED_IDENTITY.since}</span>
        </div>
      </Card>

      <Card style={{ padding: 16 }}>
        <div className="flex items-center gap-2 mb-3">
          <ExternalLink size={14} style={{ color: C.violet }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Richieste di apprendimento</span>
        </div>
        <p style={{ color: C.mutedDim, fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>
          Quando VIKI incontra un vuoto di conoscenza, lo segnala qui invece di rispondere a caso.
        </p>
        <div className="flex flex-col gap-2">
          {LEARNING_REQUESTS.map((l) => {
            const st = LEARNING_STATUS_STYLE[l.status];
            return (
              <div key={l.id} className="flex items-start gap-3 p-2.5 rounded-md" style={{ background: C.surfaceAlt }}>
                <div className="flex-1 min-w-0">
                  <div style={{ color: C.text, fontSize: 12, lineHeight: 1.5 }}>{l.gap}</div>
                  <div style={{ color: C.mutedDim, fontSize: 10.5, marginTop: 3 }}>
                    chiesto a {l.askedTo}{l.result ? ` · ${l.result}` : ""}
                  </div>
                </div>
                <span className="shrink-0" style={{ color: st.color, fontSize: 10.5, background: st.bg, padding: "3px 9px", borderRadius: 20, fontWeight: 600 }}>{l.status}</span>
              </div>
            );
          })}
        </div>
      </Card>

      <Card style={{ padding: 16 }}>
        <div className="flex items-center gap-2 mb-2">
          <HardDrive size={14} style={{ color: C.cyan }} />
          <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>Come funziona ora</span>
        </div>
        <p style={{ color: C.muted, fontSize: 12, lineHeight: 1.6 }}>
          In questa fase la memoria di Funzioni, Suggerimenti e Integrazioni è salvata nello storage condiviso dell'artifact,
          e sopravvive al refresh. L'architettura finale collegherà questi stessi pannelli a un database vero sul backend,
          così la memoria di VIKI diventerà definitiva e non legata a questo prototipo.
        </p>
      </Card>
    </div>
  );
}

/* ================= Performance ================= */
function PerformanceTab() {
  return (
    <div className="p-6 flex flex-col gap-4">
      <SectionIntro icon={Info} title="Performance">
        quanto VIKI lavora e quanto è precisa, ciclo dopo ciclo. Numeri onesti: la crescita reale, non un punteggio gonfiato.
      </SectionIntro>

      <Card style={{ padding: 20 }}>
        <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Task gestiti e precisione nel tempo</div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={ACTIVITY_TREND}>
            <defs>
              <linearGradient id="taskGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.cyan} stopOpacity={0.35} />
                <stop offset="100%" stopColor={C.cyan} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={C.borderSoft} vertical={false} />
            <XAxis dataKey="ciclo" stroke={C.mutedDim} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis stroke={C.mutedDim} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} />
            <Area type="monotone" dataKey="task" stroke={C.cyan} fill="url(#taskGrad)" strokeWidth={2} name="Task completati" />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      <Card style={{ padding: 20 }}>
        <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Precisione per area</div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={AREA_PERF} layout="vertical" margin={{ left: 10 }}>
            <CartesianGrid stroke={C.borderSoft} horizontal={false} />
            <XAxis type="number" stroke={C.mutedDim} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} domain={[0, 1]} tickFormatter={(v) => `${Math.round(v * 100)}%`} />
            <YAxis type="category" dataKey="area" stroke={C.mutedDim} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} width={100} />
            <Tooltip contentStyle={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} formatter={(v) => [`${Math.round(v * 100)}%`, "Precisione"]} />
            <Bar dataKey="precisione" fill={C.cyan} radius={[0, 4, 4, 0]} barSize={20} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <div className="flex gap-4 flex-wrap md:flex-nowrap">
        <StatCard icon={Percent} label="Precisione complessiva" value="67%" sub="su 34 task valutati" accent={C.cyan} />
        <StatCard icon={Activity} label="Area più solida" value="Comunicazioni" sub="66% di precisione" accent={C.cyan} />
        <StatCard icon={ChevronRight} label="Prossimo ciclo" value="7 giorni" sub="revisione manuale dei risultati" />
      </div>
    </div>
  );
}

/* ================= Asset ================= */
function AssetCard({ icon: Icon, title, analog, desc, children }) {
  return (
    <Card style={{ padding: 20 }}>
      <div className="flex items-start gap-3 mb-4">
        <div className="flex items-center justify-center shrink-0" style={{ width: 36, height: 36, borderRadius: 10, background: C.cyanDim, color: C.cyan }}>
          <Icon size={17} />
        </div>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>{title}</span>
            <span style={{ color: C.mutedDim, fontSize: 10, ...mono, background: C.surfaceAlt, padding: "2px 7px", borderRadius: 4 }}>equivalente concettuale di {analog}</span>
          </div>
          <div style={{ color: C.mutedDim, fontSize: 11.5, marginTop: 3, lineHeight: 1.5 }}>{desc}</div>
        </div>
      </div>
      {children}
    </Card>
  );
}

const ASSET_HISTORY = [
  { settimana: "Sett. 1", valore: 4 },
  { settimana: "Sett. 2", valore: 7 },
  { settimana: "Sett. 3", valore: 9 },
  { settimana: "Sett. 4", valore: 13 },
  { settimana: "Sett. 5", valore: 16 },
  { settimana: "Sett. 6", valore: 21 },
  { settimana: "Sett. 7", valore: 29 },
];

/* ---------- impronta digitale aziendale: un pattern generato dai dati reali
   di AREAPAQ (funzioni apprese, moduli connessi, suggerimenti attivi,
   conoscenza media). Stessa formula applicata a un'altra azienda darebbe
   un'altra forma: non è decorazione, è una firma che nasce dall'uso. ---------- */
function FingerprintGlyph({ size = 200 }) {
  const seedInputs = useMemo(() => {
    const fnLearned = FUNCTIONS.filter((f) => f.status === "Appresa").length;
    const suggActive = SUGGESTIONS.filter((s) => s.active).length;
    const integrConnected = PAQ_INTEGRATIONS.filter((i) => i.status === "Connesso").length;
    const knowledgeAvg = KNOWLEDGE_DOMAINS.reduce((s, k) => s + k.value, 0) / KNOWLEDGE_DOMAINS.length;
    return { fnLearned, suggActive, integrConnected, knowledgeAvg };
  }, []);

  const points = useMemo(() => {
    const { fnLearned, suggActive, integrConnected, knowledgeAvg } = seedInputs;
    const n = 24;
    const cx = 100, cy = 100, base = 46;
    const pts = [];
    for (let i = 0; i < n; i++) {
      const angle = (i / n) * 2 * Math.PI;
      const wobble =
        Math.sin(angle * fnLearned + integrConnected) * 14 +
        Math.cos(angle * suggActive * 1.7) * 10 +
        Math.sin(angle * 3 + knowledgeAvg * 10) * 8;
      const r = base + wobble;
      pts.push([cx + r * Math.cos(angle), cy + r * Math.sin(angle)]);
    }
    return pts;
  }, [seedInputs]);

  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ") + " Z";

  return (
    <div className="flex items-center gap-4 flex-wrap">
      <div style={{ width: size, height: size, position: "relative", flexShrink: 0 }}>
        <svg viewBox="0 0 200 200" width="100%" height="100%">
          <defs>
            <radialGradient id="fpGlow" cx="50%" cy="50%" r="60%">
              <stop offset="0%" stopColor={C.cyan} stopOpacity="0.28" />
              <stop offset="100%" stopColor={C.cyan} stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx="100" cy="100" r="80" fill="url(#fpGlow)" />
          {[1, 0.75, 0.5].map((s, i) => (
            <path key={i} d={path} fill="none" stroke={C.cyan} strokeWidth={i === 0 ? 1.8 : 1} strokeOpacity={i === 0 ? 0.9 : 0.25}
              transform={`translate(100 100) scale(${s}) translate(-100 -100)`} />
          ))}
          <circle cx="100" cy="100" r="3" fill={C.text} />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ color: C.text, fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>Generata da: {seedInputs.fnLearned} funzioni apprese, {seedInputs.suggActive} suggerimenti attivi, {seedInputs.integrConnected} moduli connessi</div>
        <p style={{ color: C.mutedDim, fontSize: 11.5, lineHeight: 1.6 }}>
          Cambia ogni volta che VIKI impara qualcosa di nuovo. Nessun'altra azienda avrebbe questa stessa forma,
          perché nasce dai dati specifici di AREAPAQ: è una firma, non una decorazione.
        </p>
      </div>
    </div>
  );
}

function AssetTab() {
  return (
    <div className="p-6 flex flex-col gap-4">
      <div className="flex items-start gap-2" style={{ color: C.mutedDim, fontSize: 12, maxWidth: 720 }}>
        <Info size={14} style={{ marginTop: 1, flexShrink: 0, color: C.cyan }} />
        <span>Il valore di VIKI come asset aziendale non è un numero unico: sono metriche verificabili separatamente, come per un modello linguistico si misura MMLU, GSM8K e calibrazione invece di un "QI" generico.</span>
      </div>

      <Card style={{ padding: 20 }}>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={15} style={{ color: C.cyan }} />
          <span style={{ color: C.text, fontSize: 13.5, fontWeight: 600 }}>Impronta digitale di AREAPAQ</span>
        </div>
        <FingerprintGlyph />
      </Card>

      <Card style={{ padding: 20 }}>
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp size={15} style={{ color: C.cyan }} />
            <span style={{ color: C.text, fontSize: 13.5, fontWeight: 600 }}>Valore dell'asset nel tempo</span>
          </div>
          <span className="flex items-center gap-1" style={{ color: C.cyan, fontSize: 11, background: C.cyanDim, padding: "2px 9px", borderRadius: 20, fontWeight: 700, ...mono }}>
            <ArrowUpRight size={11} /> +{ASSET_HISTORY[ASSET_HISTORY.length - 1].valore - ASSET_HISTORY[0].valore} pt da inizio
          </span>
        </div>
        <p style={{ color: C.mutedDim, fontSize: 11.5, marginBottom: 10 }}>Indice composito: conoscenza, regole verificate ed esecuzione pratica, settimana per settimana.</p>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={ASSET_HISTORY}>
            <CartesianGrid stroke={C.borderSoft} vertical={false} />
            <XAxis dataKey="settimana" stroke={C.mutedDim} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis stroke={C.mutedDim} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} formatter={(v) => [`${v} pt`, "Valore asset"]} />
            <Line type="monotone" dataKey="valore" stroke={C.cyan} strokeWidth={2.5} dot={{ r: 3, fill: C.cyan }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <AssetCard icon={BookOpen} title="Conoscenza per dominio" analog="MMLU" desc="Quanto VIKI padroneggia ciascun dominio, in base all'uso reale registrato finora.">
        <div className="flex flex-col gap-2.5">
          {KNOWLEDGE_DOMAINS.slice(0, 4).map((k) => (
            <div key={k.key}>
              <div className="flex items-center justify-between mb-1">
                <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>{k.label}</span>
                <span style={{ color: C.cyan, fontSize: 12, ...mono }}>{Math.round(k.value * 100)}%</span>
              </div>
              <div style={{ height: 6, background: C.surfaceAlt, borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${k.value * 100}%`, height: "100%", background: C.cyan }} />
              </div>
            </div>
          ))}
        </div>
      </AssetCard>

      <AssetCard icon={Calculator} title="Regole verificate" analog="GSM8K" desc="Non 'ragiona bene in generale': ogni funzione o suggerimento diventato regola ha un curriculum tracciato — applicata quante volte, con quale esito.">
        <div className="flex items-center gap-4 mb-3">
          <span style={{ color: C.text, fontSize: 20, fontWeight: 700, ...mono }}>2<span style={{ color: C.mutedDim, fontSize: 13 }}>/6</span></span>
          <span style={{ color: C.mutedDim, fontSize: 11.5 }}>funzioni con dati sufficienti per essere verificate</span>
        </div>
      </AssetCard>

      <AssetCard icon={Code2} title="Esecuzione pratica" analog="HumanEval" desc="Non conta se una funzione 'sembra' corretta: conta se, applicata davvero, produce il risultato giusto senza correzioni successive.">
        <div className="grid grid-cols-2 gap-3">
          {EXECUTION_DATA.map((e) => (
            <div key={e.category} style={{ background: C.surfaceAlt, borderRadius: 8, padding: 12 }}>
              <div style={{ color: C.mutedDim, fontSize: 10.5, marginBottom: 4 }}>{e.category}</div>
              <div style={{ color: e.success !== null ? C.cyan : C.mutedDim, fontSize: 17, fontWeight: 700, ...mono }}>
                {e.success !== null ? `${Math.round(e.success * 100)}%` : "—"}
              </div>
              <div style={{ color: C.mutedDim, fontSize: 9.5, marginTop: 2 }}>
                {e.samples > 0 ? `su ${e.samples} esecuzioni` : "non ancora misurato"}
              </div>
            </div>
          ))}
        </div>
      </AssetCard>

      <div className="flex items-start gap-2 px-1" style={{ color: C.mutedDim, fontSize: 11, maxWidth: 720 }}>
        <AlertTriangle size={13} style={{ marginTop: 1, flexShrink: 0, color: C.amber }} />
        <span>Fase iniziale: i numeri sono bassi perché VIKI ha appena iniziato ad accumulare dati reali. Il valore dell'asset cresce con l'uso, non con l'annuncio.</span>
      </div>
    </div>
  );
}

/* ---------- risposte di VIKI in fase di saluto: onesta sulle proprie capacità attuali ---------- */
/* Nota: qui sotto usavamo risposte pre-scritte (vikiReply) perché nel
   prototipo Claude non c'era un backend reale a cui appoggiarsi. Ora che
   VIKI vive in AREAPAQ, le chip veloci restano locali (sono scorciatoie,
   non serve chiamare il backend solo per mostrare il digest), ma i
   messaggi liberi passano dal vero endpoint /api/viki/chat. */
function vikiDigestReply(msg, digest) {
  const m = msg.toLowerCase();
  if (m.includes("cosa") && (m.includes("fatto") || m.includes("successo"))) {
    return `Da quando non ci parlavamo: ${digest.pending} decisioni in attesa, ${digest.fnPending} funzioni da verificare, ${digest.toConnect} moduli PAQ ancora da collegare. Trovi tutto in Governance e Integrazioni.`;
  }
  return null; // null = non è una scorciatoia nota, passa alla chat reale
}

export default function VikiPage() {
  const [tab, setTab] = useState(null);
  const [paused, setPaused] = useState(false);

  // Stesso principio del widget flottante: la pagina non si mostra
  // affatto per chi non ha i requisiti, non solo i dati non caricano.
  // La Sidebar già nasconde la voce di menu per chi non ha il ruolo
  // giusto, ma questo copre anche chi arriva qui digitando l'URL.
  const [authorized, setAuthorized] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/viki/access-check")
      .then(({ data }) => { if (!cancelled) setAuthorized(Boolean(data?.authorized)); })
      .catch(() => { if (!cancelled) setAuthorized(false); });
    return () => { cancelled = true; };
  }, []);

  const activeFn = ALL_VIKI_FUNCTIONS.find((f) => f.id === tab) || null;

  if (authorized === null) return null; // in attesa della verifica: nessun lampo di contenuto
  if (!authorized) return null;

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="viki-page">
      <GlobalStyle />
      <PageHeader paused={paused} onTogglePause={() => setPaused((p) => !p)} />
      {paused && (
        <div className="flex items-center gap-2 px-4 py-2.5 mb-4 rounded-lg" style={{ background: C.roseDim, border: `1px solid ${C.rose}44` }}>
          <Power size={13} style={{ color: C.rose, flexShrink: 0 }} />
          <span style={{ color: C.rose, fontSize: 12, fontWeight: 600 }}>VIKI è in pausa: nessuna azione autonoma viene eseguita finché non la riattivi.</span>
        </div>
      )}
      <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${C.border}`, background: C.surface }}>
        {tab === null ? (
          <VikiHome onOpen={setTab} />
        ) : (
          <>
            <FunctionHeader fn={activeFn} onBack={() => setTab(null)} />
            <div>
          {tab === "overview" && <OverviewTab />}
          {tab === "functions" && <FunctionsTab />}
          {tab === "suggestions" && <SuggestionsTab />}
          {tab === "deadlines" && <DeadlinesTab />}
          {tab === "governance" && <GovernanceTab />}
          {tab === "integrations" && <IntegrationsTab />}
          {tab === "crm" && <CrmTab />}
          {tab === "meetings" && <MeetingsTab />}
          {tab === "centralino" && <CentralinoTab />}
          {tab === "staff" && <StaffTab />}
          {tab === "knowledge" && <KnowledgeTab />}
          {tab === "kb" && <KBTab />}
          {tab === "performance" && <PerformanceTab />}
          {tab === "asset" && <AssetTab />}
          {tab === "memory" && <MemoryTab />}
          {tab === "guardaroba" && <GuardarobaTab />}
          {tab === "onboarding" && <OnboardingTab />}
          {tab === "simulation" && <SimulationTab />}
          {tab === "dottore" && (
            <div className="px-4 sm:px-6 pb-8" data-testid="viki-dottore-tab">
              <div className="flex items-center gap-2 mt-8 mb-[-16px]">
                <span className="font-heading uppercase" style={{ color: "#FACC15", fontSize: 12, letterSpacing: 1.2, fontWeight: 700 }}>Check</span>
              </div>
              <DoctorCheck />
              <div className="flex items-center gap-2 mt-10 mb-[-16px]">
                <span className="font-heading uppercase" style={{ color: "#FACC15", fontSize: 12, letterSpacing: 1.2, fontWeight: 700 }}>Cura</span>
              </div>
              <PlaybookSection />
              <div className="flex items-center gap-2 mt-10 mb-[-16px]">
                <span className="font-heading uppercase" style={{ color: "#FACC15", fontSize: 12, letterSpacing: 1.2, fontWeight: 700 }}>Diario</span>
              </div>
              <CureDiary />
              <div className="flex items-center gap-2 mt-10 mb-[-8px]">
                <span className="font-heading uppercase" style={{ color: "#FACC15", fontSize: 12, letterSpacing: 1.2, fontWeight: 700 }}>Suite di verifica</span>
              </div>
              <VikiSuiteSection />
            </div>
          )}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
